import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { DataStore, syncExpression, SortDirection } from "@aws-amplify/datastore";
import { Submissions, Forms, Projects, Users, Accounts, Events, Tokens, Logs, Assets, EventsSourceType, LogCategory, LogType, ModelType, FormTypes } from "./models";
import { DataContext, DataProvider } from './DataContext';
import { SnackbarProvider } from "./components/SnackbarContext.js";
import { withAuthenticator, Authenticator } from "@aws-amplify/ui-react";
import { Amplify } from "aws-amplify";
import { generateClient } from "aws-amplify/api";
//import { Storage, getUrl } from '@aws-amplify/storage';
import { getCurrentUser, fetchUserAttributes, fetchAuthSession} from "aws-amplify/auth";
//import { API, graphqlOperation } from 'aws-amplify';
//import { onUpdateSubmissions } from '../graphql/subscriptions';
import { Hub } from "aws-amplify/utils";
import { useSnackbar } from "./components/SnackbarContext.js";
import AlertSnackbar from "./components/AlertSnackbar.js";
import { AiComparisonPopup } from "./components/aiReview/AiComparisonPopup.js";
import { AiReviewPopup } from "./components/aiReview/AiReviewPopup.js";
import DataGrid from "./components/DataGrid/Datagrid.js";
import DynamicIsland from "./components/DynamicIsland/DynamicIsland.js"; // Adjust the path based on your folder structure
import Header from "./components/header.js";
import awsConfig from "./aws-exports";
import ViewFields from "./fields/ViewFields";
import DictionaryResolver from "./dictionary/DictionaryResolver";
import { hasAssetEntries } from './asset/AssetsRegistry';
import { initAssetRegistry, getAssetEntryByKey } from './asset/AssetsRegistry';
import PaintbrushLoadingScreen from './components/LoadingScreen.js'; // Adjust the path if needed
import * as AppHelperFunctions from "./AppHelperFunctions.js";
import * as DatastoreHelpers from "./helpers/DatastoreHelpers";
import * as LogHelpers from "./helpers/LogHelpers";
import * as LocalStorageHelpers from "./helpers/LocalStorageHelpers";
import * as TokenHelpers from "./helpers/TokenHelpers";
import * as FormHelpers from "./helpers/FormHelpers";
import * as ProjectHelpers from "./helpers/ProjectHelpers";
import * as SelectorHelpers from "./helpers/SelectorHelpers";
import * as ViewHelpers from "./helpers/ViewHelpers";
import * as TreeHelpers from "./helpers/TreeHelpers";
import * as AssignmentHelpers from "./helpers/AssignmentHelpers";
import * as EventHelpers from "./helpers/EventHelpers";
import * as UserHelpers from "./helpers/UserHelpers";
import * as SubmissionMutationHelpers from "./helpers/SubmissionMutationHelpers";
import * as FieldColumnsHelpers from "./helpers/FieldColumnsHelpers";
import { fetchReviewingViewData, fetchUsersViewData } from "./components/DataGrid/DataGridHelper.js";
import { getLinkedSubmissionId } from "./utils/AiReviewHelpers.js";
import { shouldPersistViewEdits } from "./permissions/Permissions.js";
import { analyseAbstract } from "./graphql/mutations.js";
//import { applyUserFlagPreset } from "./helpers/LogHelper";

import { ViewType, PermissionType, LambdaURLs, FilterConfig, ActionType } from "./Enums.js";
import '@aws-amplify/ui-react/styles.css';
import "./App.css";
import { ConsoleLogger } from '@aws-amplify/core';
//ConsoleLogger.LOG_LEVEL = 'DEBUG'; // Set the global log level to DEBUG

Amplify.configure(awsConfig);
const graphqlClient = generateClient();

DataStore.configure({
  syncPageSize: 100, // Set your desired page size here
});

// Holding page component
const HoldingPage = () => (
  <div style={{ textAlign: 'center', marginTop: '50px' }}>
    <h1>We're currently under maintenance</h1>
    <p>Please check back soon. Thank you for your patience.</p>
  </div>
);

const skipNextUpdateForId = new Set();

// Ids from a bulk operation (e.g. hide-many) we're still waiting to hear an AppSync
// confirmation for. See handleDelete and the Submissions subscription handling in
// DatastoreHelpers.js.
const pendingBatchConfirmations = new Set();

// One-shot flag: true while a pendingBatchConfirmations id was just confirmed but others
// in the same batch are still outstanding, so the next allSubmissions-derived recompute
// is known to be redundant and should be skipped.
const suppressNextDerivedRecompute = { current: false };

function safeParseJSON(value) {
  if (value == null || typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function getNestedDataValue(data, path) {
  if (!data || !path) return undefined;
  return String(path)
    .split(".")
    .reduce((acc, key) => (acc && typeof acc === "object" ? acc[key] : undefined), data);
}

function resolveAnalysisField(fieldConfig, data) {
  if (!fieldConfig?.fields?.length || !data) return "";

  const values = fieldConfig.fields
    .map((key) => {
      const value = getNestedDataValue(data, key);
      if (value && typeof value === "object" && "value" in value) return value.value;
      return value;
    })
    .filter((value) => typeof value === "string" && value.trim().length > 0);

  return values.join(fieldConfig.separator || " ").trim();
}

function stripHTML(value) {
  if (!value) return "";
  return String(value)
    .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function InnerApp() {
// — Persisted selections ——

const [user, setUser] = useState(null);
const [currentAccountToken, setCurrentAccountToken]   = useState(null);
const [currentProjectToken, setCurrentProjectToken]   = useState(null);
const [currentFormToken, setCurrentFormToken]         = useState(null);
const [currentViewLabel, setCurrentViewLabel]         = useState(null);
const [currentView, setCurrentView]                   = useState(null);
const [fieldWrapper, setFieldWrapper]                 = useState(null);
const [dictionaryResolvers, setDictionaryResolvers]   = useState(null);
const [isAssetRegistryReady, setIsAssetRegistryReady] = useState(false);
const [assetRegistryVersion, setAssetRegistryVersion] = useState(0);

// — Ephemeral, in-memory only —
  // — Ephemeral, in-memory only ——
  const [accounts, setAccounts]                   = useState([]);
  const [projects, setProjects]                   = useState([]);
  const [forms, setForms]                         = useState([]);
  const [allSubmissions, setAllSubmissions]       = useState([]);
  const [submissions, setSubmissions]             = useState([]);
  const [reviews, setReviews]                     = useState([]);
  const [hiddenSubmissions, setHiddenSubmissions] = useState([]);
  const [hiddenReviews, setHiddenReviews]         = useState([]);
  const [events, setEvents]                       = useState([]);
  const [processedFields, setProcessedFields]     = useState(null);
  const [logs, setLogs]                           = useState([]);
  const [assets, setAssets]                       = useState([]);
  const [submitters, setSubmitters]               = useState([]);
  const [reviewers, setReviewers]                 = useState([]);
  const [adminUsers, setAdminUsers]               = useState([]);
  const [allUsers, setAllUsers]                   = useState([]);
  const [filterConfig, setFilterConfig]           = useState(FilterConfig.ALL);
  const [dataFetched, setDataFetched]             = useState(false);
  const [firstStageDataFetched, setFSDF]          = useState(false);
  const [isActiveInstance, setIsActiveInstance]   = useState(false);
  const [instanceLifeMins, setInstanceLifeMins]   = useState(null);
  const [submissionToken, setSubmissionToken]     = useState(null);
  const [reviewingTokens, setReviewingTokens]     = useState([]);
  const [activeReviewToken, setActiveReviewToken] = useState(null);
  const [availableReports, setAvailableReports]   = useState(null);
  const [availableOptions, setAvailableOptions]   = useState(null);
  const [rows, setRows]                           = useState([]);
  const [selectedRows, setSelectedRows]           = useState([]);
  const [isAnalysingSelected, setIsAnalysingSelected] = useState(false);
  const [tickerItems, setTickerItems]             = useState([]);
  const [notifications, setNotifications]         = useState([]);
  const [searchTerm, setSearchTerm]               = useState('');
  const [loadingComplete, setLoadingComplete]     = useState(false);
  const subscriptionsRef                          = useRef([]);
  const fieldWrapperRef                           = useRef(null);
  const lastProcessedEventAt                      = useRef(getLastProcessedEventAt());
  const saveInFlightForId                         = useRef(new Set());
  const pendingPatchesForId                       = useRef(new Map()); // rowId → { patches, savedVersion, formType }
  // Live currentView for subscription callbacks, avoids re-running setup on view change
  const currentViewRef                            = useRef(currentView);

  const { openSnackbar } = useSnackbar();
  const loggingFlag = true;
  const logFetchUser = true;
  const instanceCheckerFlag = useRef(true);
  const bDev = useRef(false);
  const bIgnoreLoginUser = false; //N.B. Must be set to false before release
  const currentProjectRef = useRef(null);
  // Ensure selectedViewLabel is defined (you can use a default value here if necessary)
  const selectedViewLabel = currentView ? currentView.viewLabel : '';

  // Create logger once per mount:
  const loggerRef = useRef(null);
  if (!loggerRef.current) {
    loggerRef.current = LogHelpers.createLogger({
      enabled: loggingFlag,
      includeCaller: true,
    });
  }
  const logger = loggerRef.current;

  const appUser = useMemo(() => (user ? UserHelpers.buildAppUser(user, bIgnoreLoginUser) : null), [user]);

  useEffect(() => {
    if (!user) return;

    try {
      LocalStorageHelpers.setLocalStorageValue("user", user, user); 
    } catch {}
  }, [user]);

  // N.B. Temporary useEffect - just to see the contents of the enhanced appUser object
  useEffect(() => {
    if (!appUser) return;
    console.log("BU: appUser rebuilt", appUser);
  }, [appUser]);

  // SET parsedAdmin only when appUser.projectAdmin changes
  const parsedAdmin = useMemo(() => {
    const projectAdmin = user?.projectAdmin;
    if ( !projectAdmin || typeof projectAdmin !== "string" ||  projectAdmin.trim() === "" )  return null;
    return TokenHelpers.parseProjectAdmin(projectAdmin);
  }, [user?.projectAdmin]);

  // 🌅 WATCH FOR DAWN SNAPSHOT UPDATES
  useEffect(() => {
    if (!user?.dawnSnapshot) return;

    try {
      const snapshot = typeof user.dawnSnapshot === "string"
        ? JSON.parse(user.dawnSnapshot)
        : user.dawnSnapshot;

      // Check if this is a recent snapshot (last 5 minutes)
      const generatedAt = new Date(snapshot.lastSummaryGeneratedAt);
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);

      if (generatedAt > fiveMinutesAgo) {
        console.log("🌅 NEW DAWN SNAPSHOT DETECTED!");
        console.log("═".repeat(80));
        
        // AI Summary
        console.log("%c📝 AI SUMMARY:", "font-weight: bold; font-size: 14px; color: #4ECDC4;");
        console.log(snapshot.lastSummary);
        console.log("");
        
        // Raw Metrics
        console.log("%c📊 RAW METRICS:", "font-weight: bold; font-size: 14px; color: #95E1D3;");
        console.table({
          "Total Submissions": snapshot.lastSnapshot?.raw?.total_submissions || 0,
          "Submissions Today": snapshot.lastSnapshot?.raw?.submissions_today || 0,
          "Submissions Last 24h": snapshot.lastSnapshot?.raw?.submissions_last_24h || 0,
          "Complete Submissions": snapshot.lastSnapshot?.raw?.complete_submissions || 0,
          "Incomplete Submissions": snapshot.lastSnapshot?.raw?.incomplete_submissions || 0,
        });
        console.log("");
        
        // Reviews
        console.log("%c✅ REVIEW METRICS:", "font-weight: bold; font-size: 14px; color: #F38181;");
        console.table({
          "Total Reviews": snapshot.lastSnapshot?.raw?.total_reviews || 0,
          "Complete Reviews": snapshot.lastSnapshot?.raw?.complete_reviews || 0,
          "Incomplete Reviews": snapshot.lastSnapshot?.raw?.incomplete_reviews || 0,
          "Reviews Last 24h": snapshot.lastSnapshot?.raw?.reviews_completed_last_24h || 0,
          "Completion %": snapshot.lastSnapshot?.raw?.total_reviews > 0
            ? `${Math.round((snapshot.lastSnapshot.raw.complete_reviews / snapshot.lastSnapshot.raw.total_reviews) * 100)}%`
            : "0%",
        });
        console.log("");
        
        // Submissions by Topic
        if (snapshot.lastSnapshot?.raw?.submissions_by_topic) {
          console.log("%c📋 SUBMISSIONS BY TOPIC:", "font-weight: bold; font-size: 14px; color: #AA96DA;");
          console.table(snapshot.lastSnapshot.raw.submissions_by_topic);
          console.log("");
        }
        
        // Submissions by Format
        if (snapshot.lastSnapshot?.raw?.submissions_by_format) {
          console.log("%c🎯 SUBMISSIONS BY FORMAT:", "font-weight: bold; font-size: 14px; color: #FCBAD3;");
          console.table(snapshot.lastSnapshot.raw.submissions_by_format);
          console.log("");
        }
        
        // Screening
        console.log("%c🔍 SCREENING RESULTS:", "font-weight: bold; font-size: 14px; color: #FFD93D;");
        console.table({
          "Total Screened": snapshot.lastSnapshot?.raw?.total_screened_submissions || 0,
          "Passed": snapshot.lastSnapshot?.raw?.screening_pass_count || 0,
          "Failed": snapshot.lastSnapshot?.raw?.screening_fail_count || 0,
          "Pass Rate": snapshot.lastSnapshot?.raw?.total_screened_submissions > 0
            ? `${Math.round((snapshot.lastSnapshot.raw.screening_pass_count / snapshot.lastSnapshot.raw.total_screened_submissions) * 100)}%`
            : "0%",
        });
        console.log("");
        
        // Reviewers
        console.log("%c👥 REVIEWER OVERVIEW:", "font-weight: bold; font-size: 14px; color: #A8D8EA;");
        console.table({
          "Total Reviewers": snapshot.lastSnapshot?.raw?.total_reviewers || 0,
          "With Assignments": snapshot.lastSnapshot?.raw?.reviewers_with_assignments || 0,
        });
        console.log("");
        
        // Reviewer Details
        if (snapshot.lastSnapshot?.raw?.reviewer_details?.length > 0) {
          console.log("%c📋 REVIEWER DETAILS:", "font-weight: bold; font-size: 14px; color: #6C5CE7;");
          console.table(
            snapshot.lastSnapshot.raw.reviewer_details.map(r => ({
              Name: r.name || "Unknown",
              Email: r.email || "N/A",
              Assigned: r.assigned_count || 0,
              Completed: r.completed_count || 0,
              Progress: r.assigned_count > 0 
                ? `${Math.round((r.completed_count / r.assigned_count) * 100)}%`
                : "0%",
            }))
          );
          console.log("");
        }
        
        // Metadata
        console.log("%c⏰ SNAPSHOT METADATA:", "font-weight: bold; font-size: 14px; color: #FFFFD2;");
        console.log("Generated At:", snapshot.lastSummaryGeneratedAt);
        console.log("Timestamp:", snapshot.lastSnapshot?.timestamp);
        console.log("");
        
        console.log("═".repeat(80));
        console.log("%c🌅 Full snapshot object available below:", "color: #888;");
        console.log(snapshot);
        console.log("═".repeat(80));
        
        // Optional: Show toast notification
        // toast.success("🌅 Dawn summary updated!");
      }
    } catch (err) {
      console.error("❌ Error parsing Dawn snapshot:", err);
    }
  }, [user?.dawnSnapshot]);

  // SET currentProject based on currentProjectToken and projects
  const currentProject = useMemo(() => {    
    if ( !currentProjectToken ||  !Array.isArray(projects) || projects.length === 0 ) return null;
    const tempProject = SelectorHelpers.getInitialProject(projects, currentProjectToken);  
    console.log("CP: currentProject determined as:", tempProject);
    return tempProject;
  }, [projects, currentProjectToken]);
  
  // SET currentForm based on currentFormToken and forms
  const currentForm = useMemo(() => {
    if ( !currentFormToken || !Array.isArray(forms) || forms.length === 0 ) return null;
    const tempForm = FormHelpers.getForm(currentFormToken, forms);  
    console.log("CF: currentForm determined as:", tempForm);
    return tempForm;
  }, [forms, currentFormToken]);

  // SET currentViewFieldsKey if any of the fields change
  const currentViewFieldsKey = useMemo(() => {
  if (!currentView?.fields) return null;
  return currentView.fields
    .map(f => `${f.field}:${f.visible ? 1 : 0}:${f.width ?? ""}:${f.index}`)
    .join("|");
}, [currentView?.fields]);

  // SET customTheme based on currentProject
  const customTheme = useMemo(() => {
    if (!currentProject) return null;
    console.log("CT: currentProject changed, theme determined as:", currentProject?.theme);
    return currentProject?.theme ?? null;
  }, [currentProject]);

  // SET customTheme based on currentProject
  const projectConfig = useMemo(() => {
    if (!currentProject) return null;
    console.log("PC: currentProject changed, config determined as:", currentProject?.config);
    return currentProject?.config ?? null;
  }, [currentProject]);

  // N.B.T Why is this useEffect needed?
  useEffect(() => {
    currentProjectRef.current = currentProject;
  }, [currentProject]);

  // If assets change, re-init AssetRegistry
  useEffect(() => {
    const run = async () => {
      if (!Array.isArray(assets) || assets.length === 0) {
        if (UserHelpers.logSetAssetRegistry.current) console.log("SAI: Too early to set Asset Registry - no assets found.");

        // Only flip to false if we were previously ready
        if (isAssetRegistryReady) {
          setIsAssetRegistryReady(false);
        }
        return;
      }

      if (UserHelpers.logSetAssetRegistry.current) console.log("SAI: *START* - Trying to set Asset Registry.");

      try {
        // 1) Build in-memory index from latest AssetEntries
        await initAssetRegistry(assets);

        const ready = await hasAssetEntries();

        // 2) Track "ever ready" flag, but don't flap it on every save
        if (ready && !isAssetRegistryReady) {
          setIsAssetRegistryReady(true);
        } else if (!ready && isAssetRegistryReady) {
          setIsAssetRegistryReady(false);
        }

        // 3) On every ready registry, bump version so dependents can rebuild
        if (ready) {
          setAssetRegistryVersion((prev) => {
            const next = prev + 1;
            if (UserHelpers.logSetAssetRegistry.current) console.log("SAI: bumped assetRegistryVersion", { prev, next });
            return next;
          });
        }      

        if (UserHelpers.logSetAssetRegistry.current) console.log("SAI: *COMPLETED* - assets registry set");
      } catch (err) {
        console.error("Error initialising AssetRegistry:", err);
        if (isAssetRegistryReady) {
          setIsAssetRegistryReady(false);
        }
      }
    };

    run();
  }, [assets]);

  // N.B.T Should explain what this useEffect does.
  // Also how can we rely on currentForm inside here when it is not part of the deps?
  // Answer: because firstStageDataFetched depends on currentForm, so if currentForm changes, firstStageDataFetched will be null and this effect will bail out early.
  // Also isAssetRegistryReady depends on assets, which depend on currentForm indirectly.
  // None of this might be true so check carefully.
useEffect(() => {
  if (
    !currentProjectToken ||
    !isAssetRegistryReady ||
    !firstStageDataFetched
  ) {
    if (UserHelpers.logSetViewFieldsWrapper.current) {
      console.log("VFW: Too early. Project/assets not ready.");
    }
    return;
  }

  if (UserHelpers.logSetViewFieldsWrapper.current) {
    console.log("VFW: *START* - Trying to set Field Wrapper");
    console.log("VFW: currentProjectToken:", currentProjectToken);
  }

  try {
    const handlers = {
      onCellUpdate: handleCellUpdate,
      onFileUpdate: handleFileUpdate,
      onReviewerChange: handleReviewerUpdate,
      onOpenAiReviewPanel: handleOpenAiReviewPanel,
    };

    const wrapper = ViewFields.initViewFields({
      handlers,
      theme: customTheme,
      projectConfig,
      locale: currentForm?.meta?.formLanguages?.[0] || "EN",
    });

    fieldWrapperRef.current = wrapper;
    setFieldWrapper(wrapper);

    if (UserHelpers.logSetViewFieldsWrapper.current) {
      console.log("VFW: *COMPLETED* - Field Wrapper set", wrapper);
    }
  } catch (err) {
    console.error("VFW: Failed to init Field Wrapper:", err);
  }
}, [currentProjectToken, isAssetRegistryReady, firstStageDataFetched, customTheme, projectConfig, currentForm?.meta?.formLanguages]);

  useEffect(() => {
    const viewLabel = currentView?.viewLabel;

    if (!fieldWrapper || !viewLabel || !currentViewFieldsKey) {
      if (UserHelpers.logBuildFieldDefs.current) {
        console.log("FD: Skipping build; fieldWrapper or currentView.label not ready.", {
          hasWrapper: !!fieldWrapper,
          viewLabel,
          currentView,
        });
      }
      return;
    }

    if (UserHelpers.logBuildFieldDefs.current) {
      console.log("FD: *START* - Building field definitions for view:", viewLabel, "assetRegistryVersion:", assetRegistryVersion);
    }

    // mark as loading before kicking off the async build
    setDataFetched(false);

    // capture a snapshot so we can detect stale async completions
    const localWrapper = fieldWrapper;
    const localVersion = assetRegistryVersion;
    const localView = currentView;
    const localLabel = viewLabel;
    const localFieldsKey = currentViewFieldsKey;

    (async () => {
      try {
        if (UserHelpers.logBuildFieldDefs.current) {
          console.log("FD: Building fields for view:", localLabel, "assetRegistryVersion:", localVersion);
        }

        await localWrapper.buildFieldsSync({
          fields: localView.fields,
          dictionaries: currentForm?.dictionaries, // (currently ignored by ViewFields.buildFieldsSync)
          projectConfig,
        });

        // If dependencies changed while we were awaiting, treat this build as stale
        if (
          fieldWrapper !== localWrapper ||
          assetRegistryVersion !== localVersion ||
          currentView?.viewLabel !== localLabel
        ) {
          if (UserHelpers.logBuildFieldDefs.current) {
            console.log("FD: Skipping completion for stale build; dependencies changed.", {
              startedVersion: localVersion,
              currentVersion: assetRegistryVersion,
              startedLabel: localLabel,
              currentLabel: currentView?.viewLabel,
              wrapperChanged: fieldWrapper !== localWrapper,
            });
          }
          return;
        }

        if (UserHelpers.logBuildFieldDefs.current) {
          console.log("FD: Done building fields for view:", localLabel, "assetRegistryVersion:", localVersion);
        }

        setProcessedFields(localWrapper.getProcessedFields({ requireBuilt: false }));
        setDataFetched(true);
      } catch (err) {
        console.error("FD: Failed to build fields:", err, "viewLabel:", localLabel, "assetRegistryVersion:", localVersion);
      }
    })();

    if (loggingFlag) {
      console.log("FD: *COMPLETED* - Triggered field definitions build for view:", viewLabel, "assetRegistryVersion:", assetRegistryVersion);
    }
  }, [fieldWrapper, currentView?.viewLabel, currentViewFieldsKey, assetRegistryVersion]);

  /*
  // N.B.T Although currentForm is referenced inside the useEffect, it is not included in the dependency array.  
  // This is because fieldWrapper depends on currentForm, and including it here could lead to unnecessary rebuilds.
  // SHould explain what this useEffect does.
  useEffect(() => {
    const viewLabel = currentView?.viewLabel;

    if (!fieldWrapper || !viewLabel) {
      if (UserHelpers.logBuildFieldDefs.current) console.log("FD: Skipping build; fieldWrapper or currentView.label not ready.", { hasWrapper: !!fieldWrapper, currentView, label: viewLabel });
      return;
    }

    if (UserHelpers.logBuildFieldDefs.current) console.log("FD: *START* - Building field definitions for view:", viewLabel, "assetRegistryVersion:", assetRegistryVersion);

    // mark as loading before kicking off the async build
    setDataFetched(false);

    // capture a snapshot so we can detect stale async completions
    const localWrapper = fieldWrapper;
    const localVersion = assetRegistryVersion;
    const localView = currentView;
    const localLabel = viewLabel;

    (async () => {
      try {
        if (UserHelpers.logBuildFieldDefs.current) console.log("FD: Building fields for view:", localLabel, "assetRegistryVersion:", localVersion);

        await localWrapper.buildFieldsSync({
          fields: localView.fields,
          dictionaries: currentForm?.dictionaries,
        });

        // If dependencies changed while we were awaiting, treat this build as stale
        if (fieldWrapper !== localWrapper || assetRegistryVersion !== localVersion || currentView?.viewLabel !== localLabel) {
          if (UserHelpers.logBuildFieldDefs.current) console.log("FD: Skipping completion for stale build; dependencies changed.", { startedVersion: localVersion, currentVersion: assetRegistryVersion, startedLabel: localLabel, currentLabel: currentView?.viewLabel });
          return;
        }

        if (UserHelpers.logBuildFieldDefs.current) console.log("FD: Done building fields for view:", localLabel, "assetRegistryVersion:", localVersion);

        setDataFetched(true);
      } catch (err) {
        console.error("FD: Failed to build fields:", err, "viewLabel:", localLabel, "assetRegistryVersion:", localVersion);
      }
    })();

    if (loggingFlag) console.log("FD: *COMPLETED* - Triggered field definitions build for view:", viewLabel, "assetRegistryVersion:", assetRegistryVersion);
  }, [fieldWrapper, currentView?.viewLabel, assetRegistryVersion]); // N.B.T shoud explain somewhere that although currentFrom is referenced, it is not clear how it is gauranteed available.  It is because fieldWrapper depends on it.  Is this shit depepndancy?  What happens if one of the others changes and currentForm is not available?
*/
  // Function to get the last processed timestamp from localStorage or initialize it
  function getLastProcessedEventAt() {
    const storedTimestamp = localStorage.getItem('lastProcessedEventAt');
    if (storedTimestamp) {
        return new Date(storedTimestamp);
    } else {
        // Initialize to the current date to skip existing items on first load
        return new Date();
    }
  }

  // SETS active instance - checks and manages the active instance lifecycle
// Enhanced App.js useEffect for hibernation debugging
// This assumes you have these imports in App.js:
// import * as LocalStorageHelpers from "./helpers/LocalStorageHelpers";
// (or however you're importing the helpers)

// REPLACE the existing useEffect with this version:

// Enhanced App.js useEffect for hibernation debugging
// This assumes you have these imports in App.js:
// import * as LocalStorageHelpers from "./helpers/LocalStorageHelpers";

// REPLACE the existing useEffect with this version:

  useEffect(() => {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] APP-INIT: ===== APP USEEFFECT RUNNING =====`);

    let newInstanceId = null;

    // Check for existing instance
    console.log(`[${timestamp}] APP-INIT: Calling checkExistingInstance()...`);
    const existingInstanceResult = LocalStorageHelpers.checkExistingInstance();
    console.log(`[${timestamp}] APP-INIT: checkExistingInstance() returned:`, existingInstanceResult);

    if (existingInstanceResult === -1) {
      console.log(`[${timestamp}] APP-INIT: No existing instance, creating new instance ID`);
      newInstanceId = Date.now();
      console.log(`[${timestamp}] APP-INIT: New instance ID:`, newInstanceId);

      const parsedUser = LocalStorageHelpers.getUser();
      console.log(`[${timestamp}] APP-INIT: Current user:`, parsedUser);

      LocalStorageHelpers.setLocalStorageValue("adminSystemInstance", { id: newInstanceId }, parsedUser);
      console.log(`[${timestamp}] APP-INIT: setLocalStorageValue() called with instance:`, { id: newInstanceId });

      // ✅ restore old behavior
      setIsActiveInstance(true);
    } else {
      console.log(`[${timestamp}] APP-INIT: Existing instance found (${existingInstanceResult} minutes old), NOT creating new instance`);

      // ✅ restore old behavior
      setIsActiveInstance(false);
      setInstanceLifeMins(existingInstanceResult);
    }

    // Visibility change handler
    const handleVisibilityChange = () => {
      const visTimestamp = new Date().toISOString();
      console.log(`\n[${visTimestamp}] VIS-CHANGE: ========================================`);
      console.log(`[${visTimestamp}] VIS-CHANGE: VISIBILITY CHANGED`);
      console.log(`[${visTimestamp}] VIS-CHANGE: New visibility state:`, document.visibilityState);
      console.log(`[${visTimestamp}] VIS-CHANGE: Document hidden:`, document.hidden);
      console.log(`[${visTimestamp}] VIS-CHANGE: ========================================\n`);

      if (document.visibilityState === 'visible') {
        console.log(`[${visTimestamp}] VIS-CHANGE: Page became VISIBLE`);
        console.log(`[${visTimestamp}] VIS-CHANGE: Checking for potential hibernation wake...`);

        // Check raw localStorage BEFORE checkExistingInstance clears it
        const rawInstance = localStorage.getItem("adminSystemInstance");
        console.log(`[${visTimestamp}] VIS-CHANGE: Raw instance in localStorage:`, rawInstance);

        let instanceAgeMinutes = null;
        if (rawInstance) {
          try {
            const parsed = JSON.parse(rawInstance);
            const ageMs = Date.now() - parsed.id;
            instanceAgeMinutes = Math.floor(ageMs / 60000);
            console.log(`[${visTimestamp}] VIS-CHANGE: Instance age: ${instanceAgeMinutes} minutes`);
          } catch (e) {
            console.log(`[${visTimestamp}] VIS-CHANGE: Failed to parse instance:`, e);
          }
        }

        // Now run the normal check (which may clear stale instances)
        const instanceCheck = LocalStorageHelpers.checkExistingInstance();
        console.log(`[${visTimestamp}] VIS-CHANGE: Instance check result:`, instanceCheck);

        // Reload if:
        // 1. Instance was >60 minutes old (likely hibernation), OR
        // 2. Instance was cleared as stale (instanceCheck === -1 AND we saw it was old)
        if (instanceCheck > 60 || (instanceCheck === -1 && instanceAgeMinutes !== null && instanceAgeMinutes > 60)) {
          console.log(`[${visTimestamp}] VIS-CHANGE: HIBERNATION DETECTED`);
          console.log(`[${visTimestamp}] VIS-CHANGE: Instance was ${instanceAgeMinutes || instanceCheck} minutes old`);
          console.log(`[${visTimestamp}] VIS-CHANGE: Forcing page reload to clean state...`);
          window.location.reload();
        } else if (instanceCheck === -1) {
          console.log(`[${visTimestamp}] VIS-CHANGE: No instance or fresh start - no reload needed`);
        } else {
          console.log(`[${visTimestamp}] VIS-CHANGE: Normal tab switch - instance only ${instanceCheck} minutes old`);
        }
      } else {
        console.log(`[${visTimestamp}] VIS-CHANGE: Page became HIDDEN`);
      }
    };

    console.log(`[${timestamp}] APP-INIT: Adding visibilitychange event listener`);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // beforeunload handler to remove instance
    const handleBeforeUnload = () => {
      const unloadTimestamp = new Date().toISOString();
      console.log(`\n[${unloadTimestamp}] BEFORE-UNLOAD: ========================================`);
      console.log(`[${unloadTimestamp}] BEFORE-UNLOAD: PAGE IS UNLOADING`);
      console.log(`[${unloadTimestamp}] BEFORE-UNLOAD: This should ONLY happen on refresh/close/navigate`);
      console.log(`[${unloadTimestamp}] BEFORE-UNLOAD: This should NEVER happen on hibernation`);

      const parsedUser = LocalStorageHelpers.getUser();
      console.log(`[${unloadTimestamp}] BEFORE-UNLOAD: Current user:`, parsedUser);
      console.log(`[${unloadTimestamp}] BEFORE-UNLOAD: Removing instance from localStorage...`);

      LocalStorageHelpers.removeLocalStorageValue("adminSystemInstance", parsedUser);

      console.log(`[${unloadTimestamp}] BEFORE-UNLOAD: Instance removed`);
      console.log(`[${unloadTimestamp}] BEFORE-UNLOAD: ========================================\n`);
    };

    console.log(`[${timestamp}] APP-INIT: Adding beforeunload event listener`);
    window.addEventListener("beforeunload", handleBeforeUnload);

    console.log(`[${timestamp}] APP-INIT: ===== APP USEEFFECT COMPLETE =====\n`);

    // Return cleanup function
    return () => {
      const cleanupTimestamp = new Date().toISOString();
      console.log(`\n[${cleanupTimestamp}] APP-CLEANUP: ===== CLEANUP STARTING =====`);
      console.log(`[${cleanupTimestamp}] APP-CLEANUP: Removing visibilitychange listener`);
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      // ✅ restore old behavior: cleanup instance on unmount as well
      if (newInstanceId !== null) {
        const parsedUser = LocalStorageHelpers.getUser();
        console.log(`[${cleanupTimestamp}] APP-CLEANUP: Removing instance from localStorage (unmount cleanup)...`);
        LocalStorageHelpers.removeLocalStorageValue("adminSystemInstance", parsedUser);
        console.log(`[${cleanupTimestamp}] APP-CLEANUP: Instance removed (unmount cleanup)`);
      }

      console.log(`[${cleanupTimestamp}] APP-CLEANUP: Removing beforeunload listener`);
      window.removeEventListener("beforeunload", handleBeforeUnload);

      console.log(`[${cleanupTimestamp}] APP-CLEANUP: ===== CLEANUP COMPLETE =====\n`);
    };
  }, []);

  // Get appUser from local storage or auth and set state.
  useEffect(() => {
    if (!isActiveInstance) {
      if (logFetchUser) console.log(`FU: Skipping - this tab is not the active instance (isActiveInstance=${isActiveInstance}), waiting for the active tab instead.`);
      return;
    }

    if (logFetchUser) console.log("FU: *START* Fetch User started.");

    const storedUser = LocalStorageHelpers.getLocalStorageValue("user");
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);

      // per your requirement: if logs are empty, keep them empty; ensure empty when user is from localStorage
      setLogs([]);

      if (loggingFlag) console.log("FU: *COMPLETED* User found in local storage and set in state: ", parsedUser);
      return;
    }

    const controller = new AbortController();

    const fetchUser = async () => {
      try {
        if (logFetchUser) console.log("FU: Trying to fetch user details via the Datastore");

        const currentUser = await getCurrentUser();
        if (logFetchUser) console.log("FU: Using userId:", currentUser.userId);

        const { user: rawUser, logs } = await DatastoreHelpers.hydrateUserFromDataStore({
          userId: currentUser.userId,
          logger: console, // or your logger wrapper if you have one
          logFetchUserRef: { current: !!logFetchUser },
          signal: controller.signal,
        });

        // Cache user for next boot
        //N.B. This is already being done by the useEffect above that watches user state
        ///localStorage.setItem("user", JSON.stringify(rawUser));

        setUser(rawUser);

        // per your requirement
        // ToDo check if this is what is setting off loads of tickers when user loggs in or wakes from hybernation
        setLogs(Array.isArray(logs) ? logs : []);

        if (loggingFlag) {
          console.log("FU: *COMPLETED* User loaded from datastore hydration:", rawUser);
        }
      } catch (error) {
        if (controller.signal.aborted) return;

        LogHelpers.handleError("Fetch User:" + error, appUser);
        console.error("FU: Error fetching user:", error);
      }
    };

    fetchUser();

    return () => {
      controller.abort();
    };
  }, [isActiveInstance]);

  // Set initial tokens from local storage or datastore hydration
  // N.B. Shouldn't be passing the entire appUser object around.  This is not limited to this useEffect but noted here anyway
  useEffect(() => {
    if (!appUser || !parsedAdmin) return;

    if (loggingFlag) console.log("SIAPT: *START* setting initial scope tokens (account/project)...");

    const checkLocalStorageForTokens = async () => {
      const persistedAccountToken = LocalStorageHelpers.getLocalStorageValue("accountToken", appUser);
      const persistedProjectToken = LocalStorageHelpers.getLocalStorageValue("projectToken", appUser);

      const hasScope =
        typeof persistedAccountToken === "string" &&  persistedAccountToken.trim().length > 0 &&
        typeof persistedProjectToken === "string" &&  persistedProjectToken.trim().length > 0;

      if (hasScope) {
        setCurrentAccountToken(persistedAccountToken);
        setCurrentProjectToken(persistedProjectToken);

        if (UserHelpers.logSetInitialAccountProjectToken.current) {
          console.log("SIAPT: Using persisted scope tokens", { accountToken: persistedAccountToken,  projectToken: persistedProjectToken });
          console.log("SIAPT: NOTE — formToken/viewLabel will be resolved later after forms load");
        }
      } else {
        if (UserHelpers.logSetInitialAccountProjectToken.current) {
          console.log("SIAPT: Missing/invalid scope tokens in storage; using defaults from datastore appUser", parsedAdmin);
          console.log("SIAPT: Resetting all persisted selection tokens (account/project/form/view) to avoid stale state");
        }

        // Clear everything so no stale tokens survive a scope reset
        LocalStorageHelpers.resetPersistedSelectionTokens( { accountToken: true, projectToken: true, formToken: true, viewLabel: true }, appUser );

        const { default: def } = parsedAdmin;

        if (UserHelpers.logSetInitialAccountProjectToken.current) {
          console.log("SIAPT: def", def);
          console.log("SIAPT: accountToken", def.accountToken);
          console.log("SIAPT: projectToken", def.projectToken);
          console.log("SIAPT: logging an admin action: ADMIN logged in");
        }

        const logFlag = await EventHelpers.logAdminAction({
          userId: appUser.id,
          projectToken: def.projectToken,
          isTicker: true,
          isSuperAdmin: true,
          payload: {
            source: "AdminSystem",
            type: LogType.INFO,
            category: LogCategory.LOGIN,
            entityId: appUser.id,
            projectToken: def.projectToken,
            modelType: ModelType.USER,
            description: `Admin ${appUser.givenName} ${appUser.familyName} logged in at ${new Date().toLocaleString()}`,
            setDescription: `Admin ${appUser.givenName} ${appUser.familyName} logged in at ${new Date().toLocaleString()}`,
            logData: {
              userId: appUser.id,
              email: appUser.email,
              givenName: appUser.givenName,
              familyName: appUser.familyName,
              loggedInAt: new Date().toISOString(),
            },
          },
        });

        if (UserHelpers.logSetInitialAccountProjectToken.current) console.log("SIAPT: log sent, result is:", logFlag);

        // Persist scope tokens (only) via helper
        LocalStorageHelpers.setLocalStorageValue("projectToken", def.projectToken, appUser);
        LocalStorageHelpers.setLocalStorageValue("accountToken", def.accountToken, appUser);

        // Set scope in state
        setCurrentProjectToken(def.projectToken);
        setCurrentAccountToken(def.accountToken);

        if (UserHelpers.logSetInitialAccountProjectToken.current) console.log("SIAPT: Set scope tokens from defaults", {accountToken: def.accountToken, projectToken: def.projectToken });
      }

      if (loggingFlag) console.log("SIAPT: *COMPLETE* Set account and project tokens");
    };

    checkLocalStorageForTokens();
  }, [appUser, parsedAdmin]);

  // Manual function to unsubscribe from all subscriptions
  const unsubscribeSubscriptions = () => {
    DatastoreHelpers.unsubscribeSubscriptions({
      subscriptionsRef,
      logSubscriptionsRef: UserHelpers.logSubscriptions,
      logger,
    });
  };

  //Listen for logs being updated
  useEffect(() => {
    if (!Array.isArray(logs) || logs.length === 0) return;
    //console.log("L: Listening for logs state variable to update and got hit", logs);
  }, [logs]);

  // Listen for project token change and reset the datastore and subscriptions
  useEffect(() => {
    if (!currentProjectToken || !currentAccountToken) {
      if (UserHelpers.logStartDataStoreHydration.current) logger.log("HAS:", "Current Project Token OR Current Account Token are not set, returning.");
      return;
    }

    const hydrateAndSetup = async () => {
      try {
        if (UserHelpers.logStartDataStoreHydration.current) logger.log("HAS:", "*START* - Called process project data as currentProjectToken changed.");

        if (UserHelpers.logStartDataStoreHydration.current) {
          logger.log("HAS:", "called with currentAccountToken:", currentAccountToken);
          logger.log("HAS:", "called with currentProjectToken:", currentProjectToken);
        }

        if (UserHelpers.logStartDataStoreHydration.current) logger.log("HAS:", "Hydration underway...");

        await DatastoreHelpers.hydrateAndSetupProjectScope({
          // deps
          currentAccountToken,
          currentProjectToken,

          user,     // <-- NEW: plain Users model object from state
          appUser,  // enhanced user
          reviewingTokens,
          currentViewRef,

          // refs
          bDevRef: bDev,
          subscriptionsRef,
          skipNextUpdateForId,
          pendingBatchConfirmations,
          suppressNextDerivedRecompute,
          logStartDataStoreHydrationRef: UserHelpers.logStartDataStoreHydration,
          logFetchProjectDataRef: UserHelpers.logFetchProjectData,
          logSubscriptionsRef: UserHelpers.logSubscriptions,

          // setters
          setAccounts,
          setProjects,
          setForms,
          setAllSubmissions,
          setEvents,
          setLogs,
          setAssets,
          setUser,
          setTickerItems,
          setNotifications,
          setDataFetched,

          // logger + error hook
          logger,
          handleError: LogHelpers.handleError,
        });

      } catch (error) {
        logger.error("HAS:", "Error in hydrateAndSetup:", error);
      }
    };

    hydrateAndSetup();
    if (loggingFlag) logger.log("HAS:", "*COMPLETED* - Datastore configured and just waiting for hydration.");
  }, [currentProjectToken, currentAccountToken]);

  const formsSetSig = useMemo(() => {
    if (!Array.isArray(forms) || forms.length === 0) return '';
    return forms
      .map(f => `${f.token}:${f.type ?? ''}`)
      .sort()
      .join('|');
  }, [forms]);

  // Set currentFormToken based on currentFormToken (state), persisted token, or default for current forms
  useEffect(() => {
    // Guard: forms must be present before we can validate or derive tokens
    if (!formsSetSig) {
      if (UserHelpers.logSetTokens.current) console.log("SFT: Too early to set current form token (forms empty/invalid)");
      return;
    }

    if (UserHelpers.logSetTokens.current) console.log("SFT: *START* resolving current form token...");
    // Prefer currentFormToken (state) if valid; else persisted; else default submission token
    const persistedFormToken = LocalStorageHelpers.getLocalStorageValue("formToken", appUser);
    console.log("SFT: persistedFormToken", persistedFormToken);

    const resolved = FormHelpers.resolveFormToken({
      forms,
      currentFormToken,
      persistedFormToken,
      defaultFormType: FormTypes.SUBMISSION,
    });

    const finalFormToken = resolved?.token ?? null;
    const reason = resolved?.reason ?? "unknown";

    if (!finalFormToken) {
      console.error("SFT: INTERNAL ERROR — unable to resolve form token", reason);
      return;
    }

    if (UserHelpers.logSetTokens.current) console.log("SFT: resolved currentFormToken", reason, finalFormToken);

    setCurrentFormToken(prev => (prev === finalFormToken ? prev : finalFormToken));
    LocalStorageHelpers.setLocalStorageValue("formToken", finalFormToken, appUser);

    if (UserHelpers.logSetTokens.current) console.log("SFT: *COMPLETED* current form token resolved", finalFormToken);
  }, [formsSetSig]);

  // Set topology tokens (submission, reviewing, active review) based on forms and current active review token
  useEffect(() => {
    // Guard: forms must be present before we can derive topology tokens
    if (!formsSetSig) {
      if (UserHelpers.logSetTokens.current) console.log("TT: Too early to set topology tokens (forms empty/invalid)");
      return;
    }

    if (UserHelpers.logSetTokens.current) console.log("TT: *START* deriving topology tokens from forms...");

    // 1) Submission token
    const submissionFormToken = FormHelpers.getDefaultFormToken(forms, FormTypes.SUBMISSION);
    if (!submissionFormToken) { console.error("TT: INTERNAL ERROR — no submission form token found for project"); return; }
    setSubmissionToken(prev => (prev === submissionFormToken ? prev : submissionFormToken));
    if (UserHelpers.logSetTokens.current) console.log("TT: submissionToken set", submissionFormToken);

    // 2) Reviewing tokens
    const reviewTokens = FormHelpers.getFormTokensByFormType(forms, FormTypes.REVIEW) || [];
    setReviewingTokens(prev => (LocalStorageHelpers.arrayEq(prev, reviewTokens) ? prev : reviewTokens));
    if (UserHelpers.logSetTokens.current) console.log("TT: reviewingTokens set (count)", reviewTokens, reviewTokens.length);

    // 3) Active review token (repair only if invalid for current forms)
    const defaultReviewToken = FormHelpers.getDefaultReviewFormToken(forms) ?? null;

    const resolved = FormHelpers.resolveActiveReviewToken({ reviewTokens, activeReviewToken, defaultReviewToken });

    const resolvedActiveReviewToken = resolved?.token ?? null;
    const reason = resolved?.reason ?? "unknown";

    if (resolvedActiveReviewToken !== activeReviewToken) {
      setActiveReviewToken(prev => (prev === resolvedActiveReviewToken ? prev : resolvedActiveReviewToken));
      if (UserHelpers.logSetTokens.current) console.log("TT: activeReviewToken updated", reason, resolvedActiveReviewToken ?? "null");
    } else {
      if (UserHelpers.logSetTokens.current) console.log("TT: activeReviewToken valid", reason, activeReviewToken ?? "null");
    }

    if (UserHelpers.logSetTokens.current) console.log("TT: *COMPLETED* topology tokens derived");
  }, [formsSetSig]);

  // Set currentView and currentViewLabel based on currentForm, currentViewLabel (state), persisted view label, or default for current form
  useEffect(() => {
    // Guard: need a resolved currentForm before we can validate/derive view selection
    if (!currentForm) {
      if (UserHelpers.logSetTokens.current) console.log("SV: Too early to set current view (currentForm not resolved yet)");
      return;
    }

    if (UserHelpers.logSetTokens.current) console.log("SV: *START* resolving current viewLabel/currentView...");
    const views = Array.isArray(currentForm.meta?.views) ? currentForm.meta.views : [];
    if (views.length === 0) { console.error("SV: INTERNAL ERROR — currentForm has no views"); return; }

    const defaultView = FormHelpers.getInitialView(currentForm) ?? null;
    if (!defaultView?.viewLabel) { console.error("SV: INTERNAL ERROR — no defaultView for currentForm"); return; }

    // Prefer currentViewLabel (state) if valid; else persisted; else default view
    const persistedViewLabel = LocalStorageHelpers.getLocalStorageValue("viewLabel", appUser);
    const resolved = FormHelpers.resolveViewForForm({ form: currentForm, currentViewLabel, persistedViewLabel });
    const finalView = resolved?.view ?? null;
    const finalViewLabel = resolved?.viewLabel ?? null;
    const reason = resolved?.reason ?? "unknown";

    if (!finalView || !finalViewLabel) {
      console.error("SV: INTERNAL ERROR — unable to resolve view for currentForm", reason);
      return;
    }

    if (UserHelpers.logSetTokens.current) console.log("SV: persistedViewLabel", persistedViewLabel);
    if (UserHelpers.logSetTokens.current) console.log("SV: resolved finalView", finalView);
    if (UserHelpers.logSetTokens.current) console.log("SV: resolved current view label", reason, finalViewLabel);
    if (UserHelpers.logSetTokens.current) console.log("SV: current view", currentView);

    setCurrentViewLabel(prev => (prev === finalViewLabel ? prev : finalViewLabel));
    LocalStorageHelpers.setLocalStorageValue("viewLabel", finalViewLabel, appUser);

    // Keep currentView coherent with the resolved label (no dependency on currentView needed)
    setCurrentView(prev => (prev === finalView ? prev : finalView));

    if (UserHelpers.logSetTokens.current) console.log("SV: *COMPLETED* current view resolved", finalViewLabel);
  }, [currentForm]);

  useEffect(() => {
    currentViewRef.current = currentView;
  }, [currentView]);

  // N.B.T if first stage data is feteched, why are we having currentForm as part of the deps?
  // Same could be said for currentFormToken.
  // Answer: because we want this to run if form or formtoken changes, even if firstStageDataFetched is true.
  // Also, why is firststageDataFetched part of the deps?  Because if it changes from false to true, we want this to run?  Not true.  we don't even check it in the guard
  // Also, must explain what this useEffect does.
  useEffect(() => {
    const formLanguages = currentForm?.meta?.formLanguages || ['en'];

    if (
      typeof currentFormToken !== 'string' ||
      currentFormToken.trim().length === 0 ||
      !isAssetRegistryReady ||
      !firstStageDataFetched ||
      !currentForm ||
      !Array.isArray(formLanguages) ||
      formLanguages.length === 0
    ) {
      // N.B. if the datastore fails to run, this is as far as it gets – put in some kind of retry
      if (UserHelpers.logBuildDictionaryResolver.current) {
        console.log(
          'BDR: Returning. Unable to build the dictionary resolver',
          firstStageDataFetched,
          currentFormToken,
          currentForm,
          isAssetRegistryReady,
          formLanguages
        );
      }
      return;
    }
    if (UserHelpers.logBuildDictionaryResolver.current) console.log('BDR: Attempting to build the dictionary resolver');

    (async () => {
      await DictionaryResolver.init({
        // getAssetByKey is now *internal* to DictionaryResolver via AssetsRegistry
        currentFormToken,
        formLanguages,
      });

      const getPhraseSync = (lang, key) =>
        DictionaryResolver.getPhraseSync(lang, key);

      const getPhraseAsync = (lang, key) =>
        DictionaryResolver.getPhrase(lang, key);

      setDictionaryResolvers({ getPhraseSync, getPhraseAsync });
    })();
    
    if (UserHelpers.logBuildDictionaryResolver.current) console.log('BDR: Dictionary resolver built');
  }, [ currentProjectToken, isAssetRegistryReady, firstStageDataFetched, currentForm, currentFormToken ]);  

  //Listen to model state variables updating. Filter submissions.
  useEffect(() => {
    // A bulk operation (e.g. hiding many submissions) marks this true while the AppSync
    // confirmation for one of its own rows lands but other rows in the same batch are
    // still outstanding - see subscribeToModel's pendingBatchConfirmations handling in
    // DatastoreHelpers.js. Those confirmations don't change any row's visible/hidden
    // classification (we already applied it locally), so recomputing the split for them
    // is provably redundant. Any other change to allSubmissions (an untracked id, a new
    // submission, the final confirmation that drains the batch) always runs this normally.
    if (suppressNextDerivedRecompute.current) {
      suppressNextDerivedRecompute.current = false;
      return;
    }

    if (
      !submissionToken ||
      !reviewingTokens || !Array.isArray(reviewingTokens) || reviewingTokens.length === 0 ||
      !allSubmissions || !Array.isArray(allSubmissions) || allSubmissions.length === 0
    ) {
      //return;
      //N.B. if the datastore fails to run, this is as far as it gets - put in some kind of retry
      if (UserHelpers.logSetSubmissions.current) console.log("SSR: submissionToken", submissionToken);
      if (UserHelpers.logSetSubmissions.current) console.log("SSR: reviewingTokens", reviewingTokens);
      if (UserHelpers.logSetSubmissions.current) console.log("SSR: allSubmissions", allSubmissions);
      if (UserHelpers.logSetSubmissions.current) console.log("SSR: currentView", currentView);
      if (UserHelpers.logSetSubmissions.current) console.log("SSR: reviewingTokens.length", reviewingTokens?.length);
      if (UserHelpers.logSetSubmissions.current) console.log("SSR: allSubmissions.length", allSubmissions?.length);
      if (UserHelpers.logSetSubmissions.current) console.log("SSR: Array.isArray(reviewingTokens)", Array.isArray(reviewingTokens));
      if (UserHelpers.logSetSubmissions.current) console.log("SSR: Array.isArray(allSubmissions)", Array.isArray(allSubmissions));
      if (UserHelpers.logSetSubmissions.current) console.log("SSR: Too early. One or more datasets (submissionToken, reviewingTokens, allSubmissions) are invalid, not an array, or empty OR appUser not set.");
      return;
    }

    if (loggingFlag) console.log("SSR: *START* - Trying to set SUBMISSIONS and REVIEWS as allSubmissions has changed");

    if (UserHelpers.logSetSubmissions.current) console.log("SSR: submissionToken", submissionToken);
    if (UserHelpers.logSetSubmissions.current) console.log("SSR: reviewingTokens", reviewingTokens);
    if (UserHelpers.logSetSubmissions.current) console.log("SSR: allSubmissions", allSubmissions);
    if (UserHelpers.logSetSubmissions.current) console.log("SSR: currentView", currentView);
    if (UserHelpers.logSetSubmissions.current) console.log("SSR: This is the default submissionform token for this project", submissionToken);
      
    // Prepare arrays to collect categorized submissions and reviews
    const visibleSubmissions = [];
    const hiddenSubmissionsList = [];
    const visibleReviews = [];
    const hiddenReviewsList = [];

    // Loop through all submissions once
    for (const submission of allSubmissions) {
      const isSubmission = submission.formToken === submissionToken;
      const isReview = reviewingTokens.includes(submission.formToken);

      // Route matching "submissions" to either visible or hidden
      if (isSubmission) {
        if (submission.hidden) {
          hiddenSubmissionsList.push(submission);      // Submission marked hidden
        } else {
          visibleSubmissions.push(submission);         // Submission to be shown
        }
      }

      // Route matching "reviews" to either visible or hidden
      if (isReview) {
        if (submission.hidden) {
          hiddenReviewsList.push(submission);          // Review marked hidden
        } else {
          visibleReviews.push(submission);             // Review to be shown
        }
      }
    }

    // Set state: only the visible submissions/reviews are used by the UI
    setSubmissions(visibleSubmissions);
    setReviews(visibleReviews);

    // Optionally store hidden submissions/reviews (for admin tools or future undelete)
    setHiddenSubmissions(hiddenSubmissionsList);
    setHiddenReviews(hiddenReviewsList);

    // Optional debugging log
    if (UserHelpers.logSetSubmissions.current) {
      console.log("SSR: The filtered submissions for this form", visibleSubmissions);
      console.log("SSR: The filtered reviews for this form", visibleReviews);
      console.log("SSR: The hidden submissions for this form", hiddenSubmissionsList);
      console.log("SSR: The hidden reviews for this form", hiddenReviewsList);
    }
    
    // Optional: Add logging for empty arrays
    if (visibleSubmissions.length === 0 && UserHelpers.logSetSubmissions.current) console.log("SSR: No visible submissions found for this form.");
 
    if (visibleReviews.length === 0 && UserHelpers.logSetSubmissions.current) console.log("SSR: No visible reviews found for this form.");
  
    if (loggingFlag) console.log("SSR: *COMPLETED* - Submissions and reviews set filtered.");
  }, [submissionToken, reviewingTokens, allSubmissions]);

  // Listen to model state variables updating. Set rows for datagrid.
  useEffect(() => {
    if (
      !currentView || !activeReviewToken ||
      !allUsers || !Array.isArray(allUsers) ||
      !submissions || !Array.isArray(submissions) ||
      !reviews || !Array.isArray(reviews)
    ) {
      if (UserHelpers.logSetDataGridRows.current) console.log("DGR: Too early. Datasets submissions, reviews or all Project Users are invalid or current view not yet set.");
      return;
    }

    if (loggingFlag) console.log("DGR: *START* - Trying to set datagrid's ROWS as SUBMISSIONS and REVIEWS and USERS or curret view have changed");
 
    // Compute a single `rows` array based on the current view
    let newRows = [];
    switch (currentView.type) {
      case ViewType.SUBMISSIONS_UNDER_REVIEW:
        newRows = fetchReviewingViewData(submissions, reviews, activeReviewToken);
        break;
  
      case ViewType.REVIEWS:
        newRows = reviews.map(review => {
          const submissionId = getLinkedSubmissionId(review);
          const submission   = submissions.find(s => s.id === submissionId);
          return {
            ...review,
            submission,
          };
        });
        break;
  
      case ViewType.USERS:
        newRows = fetchUsersViewData(allUsers, submissions, reviews, {}, notifications);
        break;
  
      case ViewType.SUBMISSIONS:
      default:
        newRows = submissions.map((submission) => ({
          ...submission,
          notifications: notifications.filter((n) => n?.entityId === submission.id),
        }));
        break;
    }
  
    if (UserHelpers.logSetDataGridRows.current) console.log("DGR: Rows for datagrid now set.", newRows);
    setRows(newRows);
  
    if (loggingFlag) console.log("DGR: *COMPLETED* - rows set for view", currentView.type);
  }, [ submissions, reviews, allUsers, currentView, activeReviewToken, notifications ]);
  
  // PCF-DERIVE: derived state that should NOT override the selected view
  useEffect(() => {
    if (
      typeof currentFormToken !== "string" || currentFormToken.trim().length === 0 ||
      typeof currentProjectToken !== "string" || currentProjectToken.trim().length === 0 ||
      !submissionToken ||
      (submissions === null && reviews === null) ||
      (submissions !== null && !Array.isArray(submissions)) ||
      (reviews !== null && !Array.isArray(reviews)) ||
      !Array.isArray(forms) || forms.length === 0 ||
      !Array.isArray(projects) || projects.length === 0 ||
      !appUser
    ) {
      if (UserHelpers.logProcessCurrentForm.current) {
        console.log("PCF-DERIVE: Returning (inputs not ready).", dataFetched);
      }
      return;
    }

    if (UserHelpers.logProcessCurrentForm.current) {
      console.log("PCF-DERIVE: *START* - Deriving users/reports/defaultReviewToken (no view override)");
    }

    (async () => {
      try {
        // Resolve form/project and compute derived values.
        // IMPORTANT: do not use updatedView from processCurrentForm to set selection.
        const result = FormHelpers.processCurrentForm({
          currentView: currentView ?? FormHelpers.getInitialView(FormHelpers.getForm(currentFormToken, forms)),
          currentFormToken,
          currentProjectToken,
          forms,
          projects,
          currentProject,
          submissions,
          reviews,
          submissionToken,
          activeReviewToken,
          appUser,
        });

        if (!result?.ok) return;

        const { availableReports, users, defaultReviewFormToken } = result;

        if (defaultReviewFormToken && !activeReviewToken) {
          setActiveReviewToken(defaultReviewFormToken);
        }

        setSubmitters(users.submitterData);
        setReviewers(users.reviewerData);
        setAdminUsers(users.adminData);
        setAllUsers(users.allUserData);
        setAvailableReports(availableReports);

        setFSDF(true);

        if (UserHelpers.logProcessCurrentForm.current) {
          console.log("PCF-DERIVE: *COMPLETED* - Derived users/reports/defaultReviewToken");
        }
      } catch (error) {
        console.error("PCF-DERIVE: Error:", error);
        LogHelpers.handleError("PCF-DERIVE Error: " + error, appUser);
        setDataFetched(true);
      }
    })();
  }, [submissions, reviews, projects, currentFormToken, currentProjectToken]);

  // PCF-WIDGET: update ONLY the statWidget on the currently selected view (never change selection)
  useEffect(() => {
    const v = currentView;
    if (!v) return;

    // Need the correct list depending on view type
    const isReviewView = v.type === ViewType.REVIEWS || v.type === "reviews";
    const list = isReviewView ? reviews : submissions;

    if (!Array.isArray(list)) {
      if (UserHelpers.logProcessCurrentForm.current) {
        console.log("PCF-WIDGET: Too early (submissions/reviews not ready).", { view: v?.viewLabel });
      }
      return;
    }

    // You already compute these in processCurrentForm, but we keep it local and minimal
    const total = list.length;

    const incomplete = list.filter(
      (s) =>
        s.meta?.percentComplete === undefined ||
        s.meta?.percentComplete === null ||
        s.meta?.percentComplete < 100
    ).length;

    const submitted = list.filter((s) => s.meta?.complete).length;

    // NOTE: users lengths come from state that handleViewChange/derive effect sets
    const usersLen = Array.isArray(allUsers) ? allUsers.length : 0;
    const reviewersLen = Array.isArray(reviewers) ? reviewers.length : 0;

    setCurrentView((prev) => {
      if (!prev) return prev;

      // If the user has changed view since this effect fired, do nothing
      if (prev.viewLabel !== v.viewLabel) return prev;

      const next = ViewHelpers.updateStatWidget(
        prev,
        total,
        submitted,
        incomplete,
        usersLen,
        reviewersLen
      );

      // Avoid pointless state updates
      if (next === prev) return prev;
      if (JSON.stringify(next.statWidget) === JSON.stringify(prev.statWidget)) return prev;

      return next;
    });
  }, [
    currentView?.viewLabel,
    currentView?.type,
    submissions,
    reviews,
    allUsers,
    reviewers,
  ]);
  
  // Handle project change
  const handleProjectChange = async (projectToken) => {
    if (UserHelpers.logHandleProjectChange.current) console.log("HPC: New project selected: ", projectToken);

    LocalStorageHelpers.resetPersistedSelectionTokens(
      { formToken: true, viewLabel: true },
      appUser
    );

    if (process.env.NODE_ENV === "development") {
      localStorage.removeItem("projectsData");
      localStorage.removeItem("formsData");
      localStorage.removeItem("submissionsData");
      if (UserHelpers.logHandleProjectChange.current) console.log("HPC: All objects removed from local storage");
    }

    resetProjectScopeState();
    if (UserHelpers.logHandleProjectChange.current) console.log("HPC: All current tokens and objects set to null");

    // Set the new project token, which should trigger data fetching in the useEffect hooks
    setCurrentProjectToken(projectToken);
    LocalStorageHelpers.setLocalStorageValue("projectToken", projectToken, appUser);

    if (UserHelpers.logHandleProjectChange.current) console.log("HPC: New project token set to: ", projectToken);

    if (loggingFlag) console.log("HPC: *COMPLETED* Project change handled");
  };

  // Handle view change
  const handleViewChange = async (viewLabel) => {
    try {
      if (UserHelpers.logHandleViewChange.current) console.log("HVC: New view selected: ", viewLabel);

      // Fetch selected form and view
      const { selectedForm, selectedView } = SelectorHelpers.getSelectedFormAndView(
        forms,
        currentProjectToken,
        viewLabel
      );

      if (UserHelpers.logHandleViewChange.current) console.log("HVC: selectedForm: ", selectedForm);
      if (UserHelpers.logHandleViewChange.current) console.log("HVC: selectedView: ", selectedView);

      if (!selectedForm) {
        return { error: "Form not found" };
      }

      // Update current form if necessary
      if (currentFormToken !== selectedForm.token) {
        if (UserHelpers.logHandleViewChange.current) console.log("HVC: Form has changed, setting new form to: ", selectedForm);
        setCurrentFormToken(selectedForm.token);
        LocalStorageHelpers.setLocalStorageValue("formToken", selectedForm.token, appUser);
      }

      // Process view, users, and reviewers, then update the widget and view
      if (selectedView) {
        const { allUserData, submitterData, reviewerData } = UserHelpers.processProjectUsers(
          currentProject,
          submissionToken,
          activeReviewToken,
          currentProjectToken
        );

        setSubmitters(submitterData);
        setReviewers(reviewerData);
        setAllUsers(allUserData);

        if (UserHelpers.logHandleViewChange.current) console.log("HVC: submitters: ", submitterData);
        if (UserHelpers.logHandleViewChange.current) console.log("HVC: reviewers: ", reviewerData);
        if (UserHelpers.logHandleViewChange.current) console.log("HVC: all users: ", allUserData);

        // IMPORTANT: use the freshly computed submitterData/reviewerData, not stale state values
        const updatedView = ViewHelpers.updateViewWithWidget(
          selectedView,
          reviews,
          submissions,
          submitterData,
          reviewerData
        );

        if (UserHelpers.logHandleViewChange.current) console.log("HVC: View has changed, setting new view object to: ", updatedView);

        // Keep label state in sync with the view object so the token/view reconciliation effect doesn't "repair" back.

        console.log("HVC view identity", {
  sameRef: Object.is(currentView, updatedView),
  prev: currentView?.viewLabel,
  next: updatedView?.viewLabel,
  prevType: currentView?.type,
  nextType: updatedView?.type,
});

// right after: const updatedView = ViewHelpers.updateViewWithWidget(...)

console.log("HVC: identity check", {
  sameRef_currentView_updatedView: Object.is(currentView, updatedView),
  sameRef_selectedView_updatedView: Object.is(selectedView, updatedView),
  currentViewLabel: currentView?.viewLabel,
  updatedViewLabel: updatedView?.viewLabel,
  currentType: currentView?.type,
  updatedType: updatedView?.type,
  currentFieldsRefEq: Object.is(currentView?.fields, updatedView?.fields),
  selectedFieldsRefEq: Object.is(selectedView?.fields, updatedView?.fields),
  updatedFieldsLen: Array.isArray(updatedView?.fields) ? updatedView.fields.length : null,
});
        setCurrentView(updatedView);
        setCurrentViewLabel(updatedView.viewLabel);
   console.log(" set view ->", updatedView.viewLabel);
        LocalStorageHelpers.setLocalStorageValue("viewLabel", updatedView.viewLabel, appUser);
      }
    } catch (error) {
      LogHelpers.handleError("HVC: Error handling view change: " + error, appUser);
      console.error("HVC: Error handling view change: ", error);
    }

    if (loggingFlag) console.log("HVC: *COMPLETED* View change handled"); 
  };

  // Handle account change
  const handleAccountChange = async (accountToken) => {
    if (UserHelpers.logHandleAccountChange.current) console.log("HAC: New account selected: ", accountToken);

    LocalStorageHelpers.resetPersistedSelectionTokens(
      { projectToken: true, formToken: true, viewLabel: true },
      appUser
    );

    if (process.env.NODE_ENV === "development") {
      localStorage.removeItem("submissionsData");
      localStorage.removeItem("formsData");
      localStorage.removeItem("accountsData");
      localStorage.removeItem("projectsData");
      if (UserHelpers.logHandleAccountChange.current) console.log("HAC: All objects removed from local storage");
    }

    resetProjectScopeState();
    if (UserHelpers.logHandleAccountChange.current) console.log("HAC: All current tokens and objects set to null");

    const projectToken = TokenHelpers.getProjectToken(appUser.adminTokens.tokens, accountToken);

    if (!projectToken) {
      if (UserHelpers.logHandleAccountChange.current) {
        console.log("HAC: INTERNAL ERROR — could not derive projectToken for accountToken: ", accountToken);
      }
      LogHelpers.handleError("HAC: Could not derive projectToken for accountToken: " + accountToken, appUser);
      return { error: "Project token not found for selected account" };
    }

    // Set the new account and project token, which should trigger data fetching in the useEffect hooks
    setCurrentAccountToken(accountToken);
    setCurrentProjectToken(projectToken);

    LocalStorageHelpers.setLocalStorageValue("projectToken", projectToken, appUser);
    LocalStorageHelpers.setLocalStorageValue("accountToken", accountToken, appUser);

    if (UserHelpers.logHandleAccountChange.current) console.log("HAC: Account token set to:", accountToken);
    if (UserHelpers.logHandleAccountChange.current) console.log("HAC: Project token set to:", projectToken);

    if (loggingFlag) console.log("HAC: *COMPLETED* Account change handled");
  };

  const handleRowUpdate = async (updatedRow, originalRow) => {
      LogHelpers.handleError("handleRowUpdate is in use: " , appUser);
    try {
      // Fetch the original record from state
      const originalSubmission = submissions.find(
        (submission) => submission.id === originalRow.id
      );
  
      if (originalSubmission) {
        // Update the DataStore
        await DataStore.save(
          Submissions.copyOf(originalSubmission, (updated) => {
            Object.keys(updatedRow).forEach((key) => {
              if (!["updatedAt", "createdAt", "id"].includes(key)) {
                updated[key] = updatedRow[key];
              }
            });
          })
        );
  
        // Update the context with the new submission data
        setSubmissions((prevSubmissions) =>
          prevSubmissions.map((submission) =>
            submission.id === updatedRow.id ? updatedRow : submission
          )
        );
      }
    } catch (error) {
      console.error("Error updating row: ", error);
      LogHelpers.handleError("handleRowUpdate: " + error, appUser);
      return originalRow; // Optionally return the original row if there's an error
    }
  };

  // Executes a DataStore save for a row. Called directly for the first save and from the
  // allSubmissions useEffect below when a queued (pending) save is ready to fire.
  const executeSave = async (rowId, patches, formType) => {
    saveInFlightForId.current.add(rowId);
    skipNextUpdateForId.add(rowId);

    try {
      const { baseVersion } = await SubmissionMutationHelpers.updateSubmission(rowId, patches, formType, currentAccountToken, appUser?.id);
      const existing = pendingPatchesForId.current.get(rowId);
      pendingPatchesForId.current.set(rowId, { ...existing, savedVersion: baseVersion });
      if (UserHelpers.logHandleCellUpdate.current) console.log("HCU: Save resolved for", rowId, "baseVersion:", baseVersion);
    } catch (error) {
      saveInFlightForId.current.delete(rowId);
      pendingPatchesForId.current.delete(rowId);
      console.error("HCU: Error updating cell:", error);
      LogHelpers.handleError("HCU: Handle data update reported an error:\n\n" + error, appUser);
    }
  };

  // TODO: monitor AppSync WebSocket disconnects — see docs/TODO.md

  // When AppSync confirms a save, allSubmissions updates. skipNextUpdateForId already suppressed the
  // local echo, so the first update here for a queued rowId with _version > savedVersion is the
  // AppSync confirmation — it's safe to fire the next pending save.
  useEffect(() => {
    if (pendingPatchesForId.current.size === 0) return;

    const toFire = [];
    for (const [rowId, pending] of pendingPatchesForId.current) {
      if (pending.savedVersion == null) continue; // save still in-flight, await not resolved yet
      const sub = allSubmissions.find(s => s.id === rowId);
      if (!sub || sub._version <= pending.savedVersion) continue;
      toFire.push({ rowId, patches: pending.patches, formType: pending.formType });
      pendingPatchesForId.current.delete(rowId);
      saveInFlightForId.current.delete(rowId);
    }

    for (const { rowId, patches, formType } of toFire) {
      if (patches) executeSave(rowId, patches, formType);
    }
  }, [allSubmissions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Process a change in the datagrid or detail panel for non-file fields.
  // newValue can be a scalar (single-field update) or an array of {field, value} patches
  // (e.g. authors + affiliations). Both cases route through the same queue and one DataStore save.
  const handleCellUpdate = (rowId, newValue, field) => {
    if (UserHelpers.logHandleCellUpdate.current) console.log("HCU: ParentComponent: handleCellUpdate called");
    if (UserHelpers.logHandleCellUpdate.current) console.log("HCU: rowId:", rowId, "field:", field, "newValue:", newValue);

    const patches = Array.isArray(newValue) ? newValue : [{ field, value: newValue }];
    const formType = currentForm.type;

    if (saveInFlightForId.current.has(rowId)) {
      // A save is already in-flight. Merge into pending patches — last write wins per field.
      const existing = pendingPatchesForId.current.get(rowId);
      const existingPatches = existing?.patches || [];
      const merged = [
        ...existingPatches.filter(p => !patches.some(np => np.field === p.field)),
        ...patches,
      ];
      pendingPatchesForId.current.set(rowId, { ...existing, patches: merged, formType });
      if (UserHelpers.logHandleCellUpdate.current) console.log("HCU: Queued patches for", rowId, ":", merged);
      return;
    }

    pendingPatchesForId.current.set(rowId, { patches: null, savedVersion: null, formType });
    executeSave(rowId, patches, formType);
  };

  // Handle file-related changes
  const handleFileUpdate = async (rowId, newValue, field) => {
    if (UserHelpers.logHandleFileUpdate) 
      console.log("HFU: handleFileUpdate called", { rowId, newValue, field });

    try {

      skipNextUpdateForId.add(rowId);

      // Now returns the saved model with its bumped _version
      const savedModel = await SubmissionMutationHelpers.updateFileFields(
        rowId,
        newValue,
        field,
        currentForm.type,
        currentAccountToken,
        appUser?.id
      );

      if (UserHelpers.logHandleFileUpdate) {
        console.log(
          `HFU: Submission file handling complete. ` +
          `Saved ID=${savedModel.id}, version=${savedModel._version}`
        );
      }
    } catch (error) {
      console.error("HFU: Error updating the file:", error);
      LogHelpers.handleError(
        "HFU: Handle file update reported an error:\n\n" + error,
        appUser
      );
    }
  };

// Process a request to open the AI review / assignment panel from a datagrid cell
const handleOpenAiReviewPanel = ({ row, value, fieldConfig, anchorEl }) => {
  if (UserHelpers.logHandleCellUpdate.current) console.log("HAIRP: handleOpenAiReviewPanel called");
  if (UserHelpers.logHandleCellUpdate.current) console.log("HAIRP: row:", row);
  if (UserHelpers.logHandleCellUpdate.current) console.log("HAIRP: value:", value);
  if (UserHelpers.logHandleCellUpdate.current) console.log("HAIRP: fieldConfig:", fieldConfig);
  if (UserHelpers.logHandleCellUpdate.current) console.log("HAIRP: anchorEl:", anchorEl);
  if (UserHelpers.logHandleCellUpdate.current) console.log("HAIRP: projectConfig:", projectConfig);
  if (UserHelpers.logHandleCellUpdate.current) console.log("HAIRP: currentForm:", currentForm);
  if (UserHelpers.logHandleCellUpdate.current) console.log("HAIRP: currentView:", currentView);
  if (UserHelpers.logHandleCellUpdate.current) console.log("HAIRP: appUser:", appUser);
  if (UserHelpers.logHandleCellUpdate.current) console.log("HAIRP: reviews:", reviews);
  if (UserHelpers.logHandleCellUpdate.current) console.log("HAIRP: reviewers:", reviewers);
  if (UserHelpers.logHandleCellUpdate.current) console.log("HAIRP: fieldWrapperRef.current = wrapper;:", fieldWrapperRef.current);

  try {
    if (fieldConfig?.aiComparisonState || value?.comparison) {
      AiComparisonPopup.open({
        row: row || null,
        value: value || null,
        fieldConfig: fieldConfig || null,
        comparison: fieldConfig?.aiComparisonState || value?.comparison || null,
        anchorEl: anchorEl || null,
        theme: customTheme,
        config: projectConfig,
      });

      if (UserHelpers.logHandleCellUpdate.current) {
        console.log("HAIRP: opened AI comparison popup for row", row?.id);
      }
      return;
    }

    AiReviewPopup.open({
      row: row || null,
      value: value || null,
      fieldConfig: fieldConfig || null,
      anchorEl: anchorEl || null,
      theme: customTheme,
      reviews,
      reviewers,
      config: projectConfig,
      viewfieldsInstance: fieldWrapperRef.current,
      appUser,
      onAssignSubmission: handleAssignSubmission,
    });

    if (UserHelpers.logHandleCellUpdate.current) {
      console.log("HAIRP: opened AI review popup for row", row?.id);
    }
  } catch (error) {
    console.error("HAIRP: Error opening AI review panel:", error);
    LogHelpers.handleError(
      "HAIRP: Handle AI review panel open reported an error:\n\n" + error,
      appUser
    );
  }
};

// Sets (matching on user.id) a meta user entry field to the new value.
// Special case: propKey === "reviewer" updates tokens using activeReviewToken.
// Default: upsert tempUser[propKey] = newValue on the meta user entry itself.
const handleReviewerUpdate = async (userId, newValue, propKey = "reviewer") => {
  try {
    if (UserHelpers.logHandleReviewerUpdate.current) console.log("HRU: userId: ", userId);
    if (UserHelpers.logHandleReviewerUpdate.current) console.log("HRU: newValue: ", newValue);
    if (UserHelpers.logHandleReviewerUpdate.current) console.log("HRU: propKey: ", propKey);

    // Fresh query, not currentProjectRef
    const allProjects = await DataStore.query(Projects);
    const project = allProjects.find((p) => p.token === currentProjectToken);

    if (!project) {
      throw new Error(`Datastore project not found: ${currentProjectToken}`);
    }

    if (UserHelpers.logHandleReviewerUpdate.current) {
      console.log("HRU: token/version:", project.token, project._version, project.updatedAt);
      console.log("HRU: users counts:", { topUsers: project?.users?.length, metaUsers: project?.meta?.users?.length });
      console.log("Searching for userId:", userId);
    }

    if (!project?.meta?.users || !Array.isArray(project.meta.users)) {
      throw new Error("Project meta users not found or invalid (project.meta.users)");
    }

    const userIndex = project.meta.users.findIndex((u) => u.id === userId);

    if (UserHelpers.logHandleReviewerUpdate.current) console.log("User index found:", userIndex);

    if (userIndex === -1) {
      throw new Error(`User not found in project.meta.users with userId: ${userId}`);
    }

    const tempUser = project.meta.users[userIndex];

    let updatedUser;

    switch (propKey) {
      case "reviewer": {
        const userTokens = new Set(Array.isArray(tempUser.tokens) ? tempUser.tokens : []);
        const token = activeReviewToken;

        if (newValue === "Yes") {
          userTokens.add(token);
        } else if (newValue === "No") {
          userTokens.delete(token);
        } else {
          throw new Error(`Invalid newValue: ${newValue}`);
        }

        updatedUser = {
          ...tempUser,
          tokens: Array.from(userTokens),
        };
        break;
      }

      default: {
        updatedUser = {
          ...tempUser,
          [propKey]: newValue,
        };
        break;
      }
    }

    const updatedUsers = [
      ...project.meta.users.slice(0, userIndex),
      updatedUser,
      ...project.meta.users.slice(userIndex + 1),
    ];

    // New meta object, not a nested draft assignment
    const newMeta = { ...(project.meta || {}) };
    newMeta.users = updatedUsers;

    try {
      const updatedProject = await DataStore.save(
        Projects.copyOf(project, (updated) => {
          updated.meta = newMeta;
        })
      );

      if (UserHelpers.logHandleReviewerUpdate.current) {
        console.log("HRU: Updated project after save:", JSON.stringify(updatedProject, null, 2));
      }
    } catch (error) {
      console.error("Error saving project:", error);
      throw error;
    }

    const viewLabel = FormHelpers.getViewByViewType(forms, ViewType.USERS)?.viewLabel;
    handleViewChange(viewLabel);

    const baseUserLabel =
      "User " +
      tempUser.givenName +
      " " +
      tempUser.familyName +
      " (" +
      tempUser.email +
      ")";

    const messageString =
      propKey === "reviewer"
        ? `${baseUserLabel} reviewing status was set to ${newValue}`
        : `${baseUserLabel} ${propKey} was set to ${String(newValue)}`;

    openSnackbar({
      title: "Reviewer changed",
      message: messageString,
      variant: "info",
      duration: 6000,
      backdrop: "pulse",
      compareSkins: true,
    });

    if (loggingFlag) console.log("HRU: *COMPLETED*:", messageString);
  } catch (error) {
    console.error("HRU: Error occurred when updating user:", error.message);
    LogHelpers.handleError("handleReviewerUpdate: " + error, appUser);

    openSnackbar({
      title: "Update failed",
      message: `Error occurred when updating user: ${error.message}`,
      variant: "error",
      duration: 7000,
      backdrop: "pulse",
      compareSkins: true,
    });
  }
};

  // Creates a new user by calling the lambda to create the user and then
  // the lambda to add the new user to the project users
  // DC - function updaed with error throwing and snackbar use and reliance on subscriptions to update state and the datagrid
  const handleNewUser = async (data) => {    
    try { 
      if (UserHelpers.logHandleNewUser.current) console.log("HNU: Received data from child component:", data);
  
      const projectAdmin = !!data?.projectAdmin;
      const eventType = projectAdmin ? "adminUserCreated" : "userCreated";  // Correct dynamic event type
      const currentUserId = user.id;
      const newUserDetails = data;
      const newFamilyName = data.familyName;
      const newGivenName = data.givenName;
      const newEmail = data.email;
  
      // Calculate the expireAt time (90 days from now) in epoch second format
      const expire_at = Math.floor((Date.now() + 90 * 24 * 60 * 60 * 1000) / 1000);
  
      // Log each attribute being passed into the Events object
      if (UserHelpers.logHandleNewUser.current) {
        console.log("HNU: Event Type:", eventType);
        console.log("HNU: Current User ID:", currentUserId);
        console.log("HNU: Project Token:", currentProjectToken);
        console.log("HNU: Account Token:", currentAccountToken);
        console.log("HNU: New User Details:", newUserDetails);
        console.log("HNU: TTL (expire_at):", expire_at);
      }  

      const logFlag = await EventHelpers.raiseEvent({
        // top-level args
        event: eventType,
        sourceId: `${EventsSourceType.USER}:${currentUserId}`,  // Combined EventsSourceType with colon and userId
        sourceType: EventsSourceType.USER,
        projectToken: currentProjectToken,
        userId: currentUserId,
        // your business payload:
        payload: {
          setDescription: `New ${projectAdmin === true ? "ADMIN " : ""}user ${newGivenName} ${newFamilyName} (${newEmail}) created.`,           
          category: LogCategory.CREATED,
          modelType: ModelType.USER,
          familyName: newFamilyName,
          givenName: newGivenName,
          email: newEmail,
          token: projectAdmin ? currentProjectToken : currentFormToken,
          projectToken: currentProjectToken,
          accountToken: currentAccountToken,
          language: "en",
          ...(projectAdmin && { projectAdmin: true }),
          logData: {
            email:      newEmail,
            givenName:  newGivenName,
            familyName: newFamilyName,
            token: projectAdmin ? currentProjectToken : currentFormToken,
        projectToken: currentProjectToken,
        accountToken: currentAccountToken,
        ...(projectAdmin && { projectAdmin: true }),
          },
          ozLog: { isTicker: true, isSuperAdmin: true }
        }
      });

    } catch (error) {
      console.error("HNU: Failed to add user to the project:", error.message);
      LogHelpers.handleError("HNU: Failed to add user to the project:" + error, appUser);
      // Show a generic error message to the user
      openSnackbar(`An error occurred while adding the user to the project. Please try again later or contact support.`);
    }
  };  
  
  // Handles a message passed down by the dynamin island
  // Creates a new message if it doesn't exist.
  // Updates the existing message if it does exist
  // The passed in Messages is new or updated and needs to be saved back to the form
  // N.B.T. why is currentForm being derrived here when we alredy have it as a usememo
  const handleMessageUpdate = async (message) => {
    try { 
      if (UserHelpers.logHandleMessageUpdate) console.log("HMU: Message to update", message);// Fetch the original record based on view type
      const currentForm = FormHelpers.getForm(currentFormToken, forms);
      await FormHelpers.updateMessage(currentForm, message);
      if (UserHelpers.logHandleMessageUpdate) console.log("HMU: Message update complete."); 
      openSnackbar(`Message updated.`);
    
    } catch (error) {
      console.error("HMU: Failed to update a message:", error.message);
      LogHelpers.handleError("HMU: Failed to update a message:" + error, appUser);
      // Show a generic error message to the user
      openSnackbar(`An error occurred while saving/updating the email message. Please try again later or contact support.`);
    }
  };

  // TODO: move to EventHelpers.js — see docs/TODO.md
  const handleResetPassword = async (targetUser, newPassword) => {
    try {
      const currentUserId = user.id;
      await EventHelpers.raiseEvent({
        event: "AdminSetUserPassword",
        sourceType: EventsSourceType.USER,
        sourceId: `${EventsSourceType.USER}:${currentUserId}`,
        userId: currentUserId,
        projectToken: currentProjectToken,
        payload: {
          userId: targetUser.id,
          email: targetUser.email,
          projectToken: currentProjectToken,
          newPassword,
          modelType: ModelType.USER,
          category: LogCategory.RESET,
        },
      });
      openSnackbar(`Password reset requested for ${targetUser.email} — processing.`);
    } catch (error) {
      console.error("HRP: Failed to reset password:", error.message);
      LogHelpers.handleError("HRP: Failed to reset password:" + error, appUser);
      openSnackbar(`An error occurred while resetting the password. Please try again later or contact support.`);
    }
  };

  // TODO: move to EventHelpers.js — see docs/TODO.md
  const handleSendPasswordResetEmail = async (targetUser) => {
    try {
      const currentUserId = user.id;
      await EventHelpers.raiseEvent({
        event: "AdminSendPasswordResetEmail",
        sourceType: EventsSourceType.USER,
        sourceId: `${EventsSourceType.USER}:${currentUserId}`,
        userId: currentUserId,
        projectToken: currentProjectToken,
        payload: {
          userId: targetUser.id,
          email: targetUser.email,
          projectToken: currentProjectToken,
          modelType: ModelType.USER,
          category: LogCategory.SEND,
          setDescription: `Password reset email sent to ${targetUser.email}`,
        },
      });
      openSnackbar(`Password reset email requested for ${targetUser.email} — processing.`);
    } catch (error) {
      console.error("HSPRE: Failed to send password reset email:", error.message);
      LogHelpers.handleError("HSPRE: Failed to send password reset email:" + error, appUser);
      openSnackbar(`An error occurred while sending the password reset email. Please try again later or contact support.`);
    }
  };

  // TODO: move to EventHelpers.js — see docs/TODO.md
  const handleSetProjectAdmin = async (targetUser) => {
    try {
      const currentUserId = user.id;
      await EventHelpers.raiseEvent({
        event: "AdminSetProjectAdmin",
        sourceType: EventsSourceType.USER,
        sourceId: `${EventsSourceType.USER}:${currentUserId}`,
        userId: currentUserId,
        projectToken: currentProjectToken,
        payload: {
          userId: targetUser.id,
          email: targetUser.email,
          givenName: targetUser.givenName,
          familyName: targetUser.familyName,
          projectToken: currentProjectToken,
          accountToken: currentAccountToken,
          token: currentProjectToken,
          modelType: ModelType.USER,
          category: LogCategory.PERMISSIONS_UPDATED,
          setDescription: `${targetUser.email} granted admin on project ${currentProjectToken}`,
          ozLog: { isTicker: true, isSuperAdmin: true },
        },
      });
      openSnackbar(`${targetUser.email} is now an admin on this project.`);
    } catch (error) {
      console.error("HSPA: Failed to set project admin:", error.message);
      LogHelpers.handleError("HSPA: Failed to set project admin:" + error, appUser);
      openSnackbar(`An error occurred while granting admin access. Please try again later or contact support.`);
    }
  };

  const assignSubmissionsToReviewers = async ({
    submissionIds,
    reviewerIds,
    aiAssignmentApprovedByAdmin = false,
  }) => {
    try {
      const normalizedSubmissionIds = Array.isArray(submissionIds)
        ? submissionIds.filter(Boolean)
        : [];
      const normalizedReviewerIds = Array.isArray(reviewerIds)
        ? reviewerIds.filter(Boolean)
        : [];
      const templateName = "submission_for_review_json"; // N.B. DC Hardcoded for now
      const templateFormToken = submissionToken; // N.B. DC Hardcoded for now
      const isTicker = true; // N.B. DC Hardcoded for now
      const isSuperAdmin = false;
      const modelType = ModelType.REVIEW;
      const category = LogCategory.REVIEW_ASSIGNED;
      const description = "Assignments raised for review.";
      const setDescription = "${counts.submissions} submissions assigned to ${counts.reviewers} reviewers. ${counts.assignments} assignments created.";

      if (normalizedSubmissionIds.length === 0) {
        throw new Error("assignSubmissionsToReviewers: submissionIds must be a non-empty array");
      }
      if (normalizedReviewerIds.length === 0) {
        throw new Error("assignSubmissionsToReviewers: reviewerIds must be a non-empty array");
      }

      if (UserHelpers.logHandleAssignments.current) console.log("HA: reviewerIds: ", normalizedReviewerIds);
      if (UserHelpers.logHandleAssignments.current) console.log("HA: submissionIds: ", normalizedSubmissionIds);
      if (UserHelpers.logHandleAssignments.current) console.log("HA: reviews: ", reviews);
      if (UserHelpers.logHandleAssignments.current) console.log("HA: activeReviewToken: ", activeReviewToken);
      if (UserHelpers.logHandleAssignments.current) console.log("HA: currentProjectToken: ", currentProjectToken);
      if (UserHelpers.logHandleAssignments.current) console.log("HA: currentAccountToken: ", currentAccountToken);
      if (UserHelpers.logHandleAssignments.current) console.log("HA: appUser.id: ", appUser?.id);
      if (UserHelpers.logHandleAssignments.current) console.log("HA: templateName: ", templateName);
      if (UserHelpers.logHandleAssignments.current) console.log("HA: templateFormToken: ", templateFormToken);
      if (UserHelpers.logHandleAssignments.current) console.log("HA: isTicker: ", isTicker);
      if (UserHelpers.logHandleAssignments.current) console.log("HA: isSuperAdmin: ", isSuperAdmin);
      if (UserHelpers.logHandleAssignments.current) console.log("HA: modelType: ", modelType);
      if (UserHelpers.logHandleAssignments.current) console.log("HA: category: ", category);
      if (UserHelpers.logHandleAssignments.current) console.log("HA: description: ", description);

      const res = await AssignmentHelpers.raiseAssignmentsEvent({
        reviewerIds: normalizedReviewerIds, submissionIds: normalizedSubmissionIds, reviews, formToken: activeReviewToken, projectToken: currentProjectToken,
        accountToken: currentAccountToken, creatorGuid: appUser.id, templateName, templateFormToken, isTicker,
        isSuperAdmin, modelType, category, description, setDescription, aiAssignmentApprovedByAdmin,
      });

      if (res?.skipped) {
        if (UserHelpers.logHandleAssignments.current) console.log("HA: skipped:", res.message); openSnackbar(res.message);
        return res;
      }

      setSelectedRows((prev) => prev.filter((id) => !normalizedSubmissionIds.includes(id)));
      openSnackbar({
        title: "Assignments",
        message: res?.message || "Assignments created.",
        variant: "success",
        duration: 7000,
        backdrop: "pulse",
        compareSkins: true,
      });
      return res;
    } catch (error) {
      console.error("HA: Error during constructing assignSubmissions event and sending it:", error);
      LogHelpers.handleError("handleAssignments: " + error, appUser);
      openSnackbar({
        title: "Assignments",
        message: `Error occurred, please contact support - they should have more information.`,
        variant: "error",
        duration: 7000,
        backdrop: "pulse",
        compareSkins: true,
      });
      throw error;
    }
  };

  // Handles the assignment of submissions to reviewers
  const handleAssignments = async (reviewerIds) => {
    return assignSubmissionsToReviewers({ submissionIds: selectedRows, reviewerIds });
  };

  const handleAssignSubmission = async ({
    submissionId,
    reviewerIds,
    aiAssignmentApprovedByAdmin = false,
  }) => {
    return assignSubmissionsToReviewers({
      submissionIds: submissionId ? [submissionId] : [],
      reviewerIds,
      aiAssignmentApprovedByAdmin,
    });
  };
  
  const handleStatClick = (filterType) => {
    console.log(`Filter Type Clicked: ${filterType}`); // This logs what filter was clicked
    setFilterConfig(filterType);
  };
    
  const setActiveReviewTokenHandler = (token) => {
    console.log(`Active review token changed: ${token}`); // This logs what token was clicked
    setActiveReviewToken(token);

    if (currentView) {
      const { allUserData, submitterData, reviewerData } = UserHelpers.processProjectUsers(
        currentProject,
        submissionToken,
        token
      );
      setSubmitters(submitterData);
      setReviewers(reviewerData);
      setAllUsers(allUserData);
      
    if (UserHelpers.logProcessProjectData.current) console.log("PPD: submitters: ", submitters);
    if (UserHelpers.logProcessProjectData.current) console.log("PPD: reviewers: ", reviewers);
    if (UserHelpers.logProcessProjectData.current) console.log("PPD: allUserData: ", allUserData);


      const isReviewView = currentForm.type === FormTypes.REVIEW;
      const submissionNumbers = isReviewView ? reviews.length : submissions.length;
      const inCompletedFilteredSubmissionsLength = isReviewView 
        ? reviews.filter(submission => submission.meta.percentComplete === undefined || submission.meta.percentComplete === null || submission.meta.percentComplete < 100).length
        : submissions.filter(submission => submission.meta.percentComplete === undefined || submission.meta.percentComplete === null || submission.meta.percentComplete < 100).length;
      const submittedFilteredSubmissionsLength = isReviewView ? reviews.filter(submission => submission.meta.complete).length : submissions.filter(submission => submission.meta.complete).length;
      const updatedView = ViewHelpers.updateStatWidget(
        currentView,
        submissionNumbers,
        submittedFilteredSubmissionsLength,
        inCompletedFilteredSubmissionsLength,
        submitterData.length,
        reviewerData.length
      );
      setCurrentView(updatedView);
        
        localStorage.setItem('viewLabel', updatedView.viewLabel);
    }
  };

  const handleLoadingComplete = () => {
    setLoadingComplete(true); // Set loadingComplete to true when loading animation is done
  };

  const handleClearInstance = () => {
    const confirmed = window.confirm(
      `Warning: This will reset the admin system instance and may cause issues if another instance is active.\n\n` +
      `Only proceed if you are sure there is no other active instance.\n\n` +
      `Do you want to continue?`
    );
  
    if (confirmed) {
      localStorage.removeItem('adminSystemInstance');
      console.log("User clicked the clear instance button");
  
      // Define an async function within this handler to handle error logging and reload
      const performAsyncActions = async () => {
        // Get the user details
        const parsedUser = LocalStorageHelpers.getUser();
  
        // Call handleError asynchronously and wait for it to complete
        await LogHelpers.handleError("User clicked the clear instance button.", parsedUser);
  
        //N.B.stop the datastore
        // Reload after error handling is complete
        window.location.reload();
      };
  
      // Call the async function
      performAsyncActions();
    }
  };
  
  const handleSelectionChange = (newSelection) => {
    console.log("Selected Row IDs:", newSelection);
    setSelectedRows(newSelection);
  };  
  
  const handleNewProject = async (newProjectData) => {
    try {
      if (UserHelpers.logHandleNewProject.current) console.log("HNP: Received data from child component:", newProjectData);

      const res = await EventHelpers.raiseNewProjectCreatedEvent({
        userId: appUser.id,
        userEmail: appUser.email,
        accountToken: currentAccountToken,
        ...newProjectData,
      });

      openSnackbar(`The project ${res.newProjectName} (${res.newProjectToken}) has been created and will be available shortly.`);
    } catch (error) {
      console.error("HNP: Failed to add new project:", error.message);
      LogHelpers.handleError("HNP: Failed to add the new project:" + error, appUser);
      openSnackbar(`An error occurred while adding a new project. Please try again later or contact support.`);
    }
  };

  // N.B.T. why is currentForm being derrived here when we alredy have it as a usememo
  const handleSaveForm = async (partialUpdate) => {
    try {
      if (UserHelpers.logHandleFormSave.current) {
        console.log("HSF: Incoming partial update", partialUpdate);
      }

      if (!currentForm?.id) throw new Error("HSF: current form not found or missing id");
        await FieldColumnsHelpers.saveFormPartial(currentForm.id, partialUpdate);

      if (UserHelpers.logHandleFormSave.current) {
        console.log("HSF: Form saving complete.");
      }

      openSnackbar(`Form updated.`);
      return true;
    } catch (error) {
      console.error("HSF: Failed to save the form:", error?.message || error);
      LogHelpers.handleError("HSF: Failed to save the form:" + error, appUser);
      openSnackbar(
        `An error occurred while saving/updating the form. Please try again later or contact support.`
      );
      return false;
    }
  };

  const handleSaveAssets = async (updatedAssetsMap) => {
    try {
      if (UserHelpers.logHandleSaveAssets.current) console.log("HSA: handleSaveAssets called with updatedAssetsMap:", updatedAssetsMap);

      const res = await EventHelpers.upsertAssetsAndRaiseEvents({
        updatedAssetsMap,
        accountToken: currentAccountToken,
        projectToken: currentProjectToken,
        formToken: currentFormToken,
        userId: appUser?.id,
        uploadedBy: appUser?.initials,
        log: UserHelpers.logHandleAssetsSave.current === true,
      });

      if (res?.skipped) {
        if (UserHelpers.logHandleSaveAssets.current) console.log("HSA:", res?.message);
        return true;
      }

      openSnackbar(`Assets updated (${res?.count ?? 0}).`);
      return true;
    } catch (error) {
      console.error("HSA: Failed to save assets:", error?.message || error);
      LogHelpers.handleError("HSA: Failed to save assets:" + error, appUser);
      openSnackbar( "An error occurred while saving/updating the assets. Please try again later or contact support." );
      return false;
    }
  };

  // meta.views is canonical; update only there + normalize indices
  function applyOptimisticViewFields(formId, viewLabel, nextFields) {
    const normalized = Array.isArray(nextFields)
      ? nextFields.map((c, i) => ({ ...c, index: i + 1 }))
      : [];

    setForms(prev =>
      Array.isArray(prev)
        ? prev.map(f => {
            if (f.id !== formId) return f;

            const meta = f.meta || {};
            const views = Array.isArray(meta.views) ? meta.views : [];
            const idx = views.findIndex(v => v?.viewLabel === viewLabel);
            if (idx < 0) return f;

            const nextViews = views.slice();
            nextViews[idx] = { ...views[idx], fields: normalized };

            return { ...f, meta: { ...meta, views: nextViews } };
          })
        : prev
    );

    // If the edited view is currently selected, refresh it immediately.
    // viewLabel is unique across the project, so label check is enough.
    setCurrentView(prev =>
      prev && prev.viewLabel === viewLabel
        ? { ...prev, fields: normalized }
        : prev
    );
  }

const handleFieldsSave = async (nextFields, formId, viewLabel) => {
  const form = (forms || []).find((f) => f.id === formId);
  if (!form) return;

  console.log("HFS: handleFieldsSave called with formId:", formId, "viewLabel:", viewLabel, "nextFields:", nextFields); 

  const auth = {
    userId: appUser?.id,
    isSuperAdmin: appUser?.adminLevel === PermissionType.SUPER_ADMIN,
  };

  const view = FormHelpers.getViewByLabel(form, viewLabel);

  const canPersist = shouldPersistViewEdits(view, auth);
  if (!canPersist) {
    openSnackbar({
      title: view?.defaultView ? "Default view" : "View locked",
      message: "Changes to this view are not saved. Create a custom view to persist edits.",
      variant: "warn",
      duration: 6500,
      backdrop: "pulse",
    });
    return;
  }

  // Snapshot for rollback
  const prevForms = forms;
  const prevCurrentView = currentView;

  // Optimistic — datagrid reflects the change immediately
  applyOptimisticViewFields(formId, viewLabel, nextFields);

  const res = await FieldColumnsHelpers.applyFieldsChange(
    form,
    viewLabel,
    { op: "replace-all", fields: nextFields },
    { auth }
  );

  if (!res?.ok) {
    // Rollback optimistic update
    setForms(prevForms);
    setCurrentView(prevCurrentView);

    openSnackbar({
      title: "Save failed",
      message:
        res?.code === "PERMISSION_DENIED"
          ? "You don't have permission to edit this view."
          : "Could not persist field changes.",
      variant: "error",
      backdrop: "pulse",
    });
  }
};

  const handleViewsSave = async (formId, updatedViews) => {

  console.log("HVS: handleViewsSave called with formId:", formId, "updatedViews:", updatedViews); 
    await FieldColumnsHelpers.saveFormPartial(formId, {
      meta: { views: updatedViews }
    });

    setForms(prev =>
      prev.map(f =>
        f.id === formId
          ? { ...f, meta: { ...(f.meta || {}), views: updatedViews } }
          : f
      )
    );
  };

  const handleProjectsSave = async (projectToken, updatedProject) => {
    await ProjectHelpers.saveProjectPartial(projectToken, updatedProject);
  /*
    setProjects((prev) =>
      prev.map((p) =>
        p.token === projectToken ? { ...p, ...updatedProject } : p
      )
    );
    */
  };

// Width change: MUI gives { colDef, width }
const handleGridColumnWidthChange = async ({ colDef, width }) => {
  if (!colDef?.field) return;
  if (!selectedViewLabel || !currentForm?.id) return;

  const auth = {
    userId: appUser?.id,
    isSuperAdmin: appUser?.adminLevel === PermissionType.SUPER_ADMIN,
  };

  const view = FormHelpers.getViewByLabel(currentForm, selectedViewLabel);
  const canPersist = shouldPersistViewEdits(view, auth);

  if (!canPersist) {
    openSnackbar({
      title: view?.defaultView ? "Default view" : "View locked",
      message:
        "You can resize columns, but changes won’t be saved for this view.",
      variant: "warn",
      duration: 6000,
      backdrop: "pulse",
      compareSkins: true, // ← added
    });
    return;
  }

  const res = await FieldColumnsHelpers.applyFieldsChange(
    currentForm,
    selectedViewLabel,
    {
      op: "set-prop",
      field: colDef.field,
      patch: { width },
    },
    { auth }
  );

  if (!res?.ok) {
    openSnackbar({
      title: "Save failed",
      message:
        res?.code === "PERMISSION_DENIED"
          ? "You don’t have permission to edit this view."
          : "Could not persist column width.",
      variant: "error",
      backdrop: "pulse",
      compareSkins: true, // ← added
    });
  }
};

  // Order change variant A: { column, targetIndex }
const handleGridColumnMove = async ({ field, targetIndex }) => {
  if (!field?.field) return;
  if (!selectedViewLabel || !currentForm?.id) return;

  const auth = {
    userId: appUser?.id,
    isSuperAdmin: appUser?.adminLevel === PermissionType.SUPER_ADMIN,
  };

  const view = FormHelpers.getViewByLabel(currentForm, selectedViewLabel);

  const canPersist = shouldPersistViewEdits(view, auth);

  if (!canPersist) {
    openSnackbar({
      title: view?.defaultView ? "Default view" : "View locked",
      message:
        "You can reorder columns, but changes won’t be saved for this view.",
      variant: "warn",
      duration: 6000,
      backdrop: "pulse",
    });
    return;
  }

  const res = await FieldColumnsHelpers.applyFieldsChange(
    currentForm,
    selectedViewLabel,
    {
      op: "move",
      field: field.field,
      toIndex: targetIndex,
    },
    { auth }
  );

  if (!res?.ok) {
    openSnackbar({
      title: "Save failed",
      message:
        res?.code === "PERMISSION_DENIED"
          ? "You don’t have permission to edit this view."
          : "Could not persist column order.",
      variant: "error",
      backdrop: "pulse",
    });
  }
};

  // Order change variant B: array of colDefs already ordered
  const handleGridColumnReorderList = async (orderedColDefs) => {
    if (!Array.isArray(orderedColDefs)) return;

    if (!selectedViewLabel) {
      console.warn("[onColumnOrderChange(list)] selectedViewLabel not set");
      return;
    }
    if (!currentProjectToken) {
      console.warn("[onColumnOrderChange(list)] currentProjectToken not set");
      return;
    }
    if (!currentForm?.id) {
      console.warn("[onColumnOrderChange(list)] currentForm not resolved");
      return;
    }

    const res = await FieldColumnsHelpers.applyFieldsChange(
      currentForm,
      selectedViewLabel,
      {
        op: "reorder",
        fields: orderedColDefs,
      },
      {
        auth: {
          userId: appUser?.id,
          isSuperAdmin: appUser?.adminLevel === PermissionType.SUPER_ADMIN,
        },
      }
    );

    if (res?.ok) {
      applyOptimisticViewFields (currentForm.id, selectedViewLabel, res.fields);
      return;
    }

    console.error("[onColumnOrderChange(list)] persist error:", {
      code: res?.code,
      error: res?.error,
    });

    // Handler owns user messaging; wire to your snackbar/toast here
    // if (res?.code === "PERMISSION_DENIED") showSnackbar("You don’t have permission to edit this view.", { variant: "warning" });
    // else showSnackbar("Save failed.", { variant: "error" });
  };

  const treeData = useMemo(() => {
    if (
      !appUser?.adminTokens?.tokens ||
      typeof appUser.adminTokens.tokens !== "object" ||
      Object.keys(appUser.adminTokens.tokens).length === 0 ||
      !currentAccountToken ||
      !Array.isArray(accounts) || accounts.length === 0 ||
      !Array.isArray(projects) || projects.length === 0 ||
      !Array.isArray(forms) || forms.length === 0 ||
      !currentProjectToken ||
      !selectedViewLabel
    ) {
      if (UserHelpers.logBuildTreeData.current) console.log("BTD: One of the dependencies is missing, returning null");
      //if (UserHelpers.logBuildTreeData.current) console.log("BTD: appUser?.adminTokens", appUser?.adminTokens);
      //if (UserHelpers.logBuildTreeData.current) console.log("BTD: currentAccountToken", currentAccountToken);
      //if (UserHelpers.logBuildTreeData.current) console.log("BTD: accounts", accounts);
      //if (UserHelpers.logBuildTreeData.current) console.log("BTD: projects", projects);
      //if (UserHelpers.logBuildTreeData.current) console.log("BTD: forms", forms);
      //if (UserHelpers.logBuildTreeData.current) console.log("BTD: currentProjectToken", currentProjectToken);
      //if (UserHelpers.logBuildTreeData.current) console.log("BTD: selectedViewLabel", selectedViewLabel);
      return null;
    }

    if (UserHelpers.logBuildTreeData.current) console.log("BTD: Building Tree Data");
      if (UserHelpers.logBuildTreeData.current) console.log("BTD: projects", projects);
      if (UserHelpers.logBuildTreeData.current) console.log("BTD: forms", forms);

    const treeData = TreeHelpers.buildTreeData({
      adminTokens: appUser?.adminTokens?.tokens,
      currentAccountToken,
      accounts,
      projects,
      forms,
      currentProjectToken,
      currentUserId: appUser?.id,
      isSuperAdmin:       !!appUser?.isSuperAdmin,
      selectedViewLabel,
    });

    if (loggingFlag) console.log("BTD: Tree Data built successfully", treeData);
    return treeData;
  }, [
    appUser?.adminTokens?.tokens,
    currentAccountToken,
    accounts,
    projects,
    forms,
    currentProjectToken,
    selectedViewLabel
  ]);

  const formData = useMemo(() => {
    if (
      !Array.isArray(forms) || forms.length === 0 ||
      !currentProjectToken
    ) {
      return null;
      if (UserHelpers.logBuildFormData.current) console.log("BFD: One of the dependencies is missing, returning null");
      if (UserHelpers.logBuildFormData.current) console.log("BFD: forms", forms);
      if (UserHelpers.logBuildFormData.current) console.log("BFD: currentProjectToken", currentProjectToken);  
      return null;
    }
    
    if (UserHelpers.logBuildFormData.current) console.log("BFD: Building Form Data", forms, currentProjectToken);    

    const formData = TreeHelpers.buildProjectFormsMinimal({
      forms,
      currentProjectToken,
    });
    
    if (loggingFlag) console.log("BFD: Form Data built successfully", formData);
    return formData;
  }, [ forms, currentProjectToken ]);

  const cloneProjectTreeData = useMemo(() => {
    if (
      !appUser?.adminTokens?.tokens ||
      typeof appUser.adminTokens.tokens !== "object" ||
      Object.keys(appUser.adminTokens.tokens).length === 0 ||    
      !Array.isArray(accounts) || accounts.length === 0 ||
      !Array.isArray(projects) || projects.length === 0
    ) {
      if (UserHelpers.logBuildTreeData.current) console.log("CPTD: One of the dependencies is missing, returning null");      
      return null;
    }

    if (UserHelpers.logBuildTreeData.current) console.log("CPTD: Building Clone Project Tree Data");   
    if (UserHelpers.logBuildTreeData.current) console.log("CPTD: projects", projects);   
    if (UserHelpers.logBuildTreeData.current) console.log("CPTD: appUser?.adminTokens", appUser?.adminTokens);   

    const treeData = TreeHelpers.buildCloneProjectTreeData({
      adminTokens:          appUser?.adminTokens?.tokens,
      accounts,
      projects
    });
    
    if (loggingFlag) console.log("CPTD: Clone Project Tree Data built successfully", treeData);
    return treeData;
  }, [appUser?.adminTokens, accounts, projects]);

  // Check the environment variable
  const isAppUnderMaintenance = process.env.REACT_APP_UNDER_MAINTENANCE === 'true';
  //console.log("isAppUnderMaintenance", isAppUnderMaintenance);

  // Conditionally render the maintenance message or the application
  if (isAppUnderMaintenance) {
    return <HoldingPage />;
  }

  if (!loadingComplete) {
    //console.log("got here with active instance of", isActiveInstance);
    return <PaintbrushLoadingScreen
      dataFetched={dataFetched}
      onLoadingComplete={handleLoadingComplete}
      isActiveInstance={isActiveInstance}
      instanceLifeMins={instanceLifeMins}
      handleClearInstance={handleClearInstance}
    />;
    } 

  const handleAnalyseSelectedSubmissions = async () => {
    if (!selectedRows?.length || isAnalysingSelected) return;

    const selectedIds = selectedRows.map((id) => String(id));
    const savedSubmissions = Array.isArray(allSubmissions) ? allSubmissions : [];
    const selectedSubmissions = selectedIds
      .map((id) => savedSubmissions.find((submission) => String(submission.id) === id))
      .filter(Boolean);

    if (selectedSubmissions.length === 0) {
      openSnackbar({
        title: "AI analysis",
        message: "No selected submissions were found in the current data.",
        variant: "warning",
      });
      return;
    }

    setIsAnalysingSelected(true);
    openSnackbar({
      title: "AI analysis",
      message: `Starting analysis for ${selectedSubmissions.length} selected submission${selectedSubmissions.length === 1 ? "" : "s"}.`,
      variant: "info",
    });

    let started = 0;
    let skipped = 0;
    let failed = 0;

    for (const submission of selectedSubmissions) {
      try {
        const form = forms.find((item) => item.token === submission.formToken);
        const formMeta = safeParseJSON(form?.meta) || {};
        const analysisConfig = formMeta?.AI?.analysis;

        if (!analysisConfig?.title || !analysisConfig?.abstract) {
          skipped += 1;
          console.warn("[AI Analysis] Missing form.meta.AI.analysis config for submission", submission.id);
          continue;
        }

        const submissionData = safeParseJSON(submission.data) || {};
        const title = stripHTML(resolveAnalysisField(analysisConfig.title, submissionData));
        const abstract = stripHTML(resolveAnalysisField(analysisConfig.abstract, submissionData));

        if (!title || !abstract) {
          skipped += 1;
          console.warn("[AI Analysis] Missing title or abstract for submission", submission.id);
          continue;
        }

        if (abstract.length < 100) {
          skipped += 1;
          console.warn("[AI Analysis] Abstract too short for submission", submission.id);
          continue;
        }

        await graphqlClient.graphql({
          query: analyseAbstract,
          variables: {
            input: {
              title,
              abstract,
              formToken: submission.formToken,
              submissionId: submission.id,
              analysisConfig: JSON.stringify(analysisConfig),
            },
          },
        });

        started += 1;
      } catch (error) {
        failed += 1;
        console.error("[AI Analysis] Failed to analyse submission", submission?.id, error);
        LogHelpers.handleError(
          `handleAnalyseSelectedSubmissions: ${submission?.id || "unknown"}: ${error}`,
          appUser
        );
      }
    }

    setIsAnalysingSelected(false);

    const parts = [];
    if (started) parts.push(`${started} started`);
    if (skipped) parts.push(`${skipped} skipped`);
    if (failed) parts.push(`${failed} failed`);

    openSnackbar({
      title: "AI analysis",
      message: parts.length ? parts.join(", ") : "No analyses were started.",
      variant: failed ? "warning" : "success",
    });
  };
   
  const handleDelete = async () => {
    if (!selectedRows?.length) return;

    // Optional debugging log
    if (UserHelpers.logToggleSubmissionVisibility.current) console.log("TSV: The submissions to hide are", selectedRows);

    // Suppress the per-row local-echo update from the global Submissions subscription
    // (see subscribeToModel in DatastoreHelpers.js). Without this, each DataStore.save()
    // below triggers its own setAllSubmissions(prev => prev.map(...)) over the FULL
    // submissions list, plus a downstream re-split of visible/hidden submissions+reviews
    // in App.js's allSubmissions effect - once PER selected row. For a large selection
    // that's an O(selected x total) cascade of re-renders. We apply one bulk update instead.
    const idSet = new Set(selectedRows);
    selectedRows.forEach(id => {
      skipNextUpdateForId.add(id);
      pendingBatchConfirmations.add(id);
    });

    // Update remote only
    try {
      await AssignmentHelpers.setSubmissionsHiddenStatus(selectedRows, true, allSubmissions);

      setAllSubmissions(prev =>
        prev.map(sub => (idSet.has(sub.id) ? { ...sub, hidden: true } : sub))
      );

      setSelectedRows([]); // Optional UX cleanup
      if (UserHelpers.logSetSubmissions.current) console.log("SSR: Hidden submissions saved to DataStore.");
    } catch (err) {
      console.error("TSV: Failed to hide submissions in DataStore", err);
      // Saves that never landed won't produce a local echo or AppSync confirmation to
      // consume these - clear them so a later legitimate update to the same row isn't
      // incorrectly swallowed or left permanently pending.
      selectedRows.forEach(id => {
        skipNextUpdateForId.delete(id);
        pendingBatchConfirmations.delete(id);
      });
    }
  };
     
  const handleCycleComplete = async () => {
    setTickerItems(prev =>
      prev
        .map(i => ({ ...i, remainingCycles: i.remainingCycles - 1 }))
        .filter(i => i.remainingCycles > 0)
    );
  };  

  const handleTickerClose = () => {
    clearTickerItems(); // upstream clears the authoritative list
    console.log("Ticker manually closed by user; items cleared.");
  };
  const clearTickerItems = () => setTickerItems([]);

  // Reset all project-scoped state to force a clean re-hydration
  const resetProjectScopeState = () => {
    // Indicate that data has not been fetched, triggering the loading screen
    setDataFetched(false);
    setLoadingComplete(false);

    // Unsubscribe now that scope is changing
    unsubscribeSubscriptions();

    // Clear the rows from the datagrid/dynamic island
    setRows([]);

    // Clear all state related to the current project
    setCurrentViewLabel(null);
    setCurrentFormToken(null);
    setCurrentView(null);

    setAccounts(null);
    setProjects(null);
    setForms(null);
    setLogs(null);
    setAssets(null);

    setFieldWrapper(null);

    setSubmissions(null);
    setAllSubmissions(null);
    setReviews(null);

    setReviewers(null);
    setSubmitters(null);
    setAllUsers(null);

    setActiveReviewToken(null);

    setIsAssetRegistryReady(false); // N.B. you noted dictionaries may need reset later too
  };


 // console.log("currentProject", currentProject);
 // console.log("currentProject.accountToken", currentProject.accountToken);
//console.log("adminTokens", appUser.adminTokens);
//console.log("adminUsers", adminUsers);
//console.log("treeData", treeData);
//console.log("formData", formData);
//console.log("cloneProjectTreeData", cloneProjectTreeData);
//console.log("logs", logs);
//console.log("All submissions", allSubmissions);
//console.log("currentForm.meta.formLanguages", currentForm.meta.formLanguages);
//console.log("***assetIndex", assetIndex);
//console.log("***currentView", currentView);
//console.log("viewfieldsInstance:", fieldWrapper);
//console.log("rows", rows);

  return (
    <div>
      <AlertSnackbar />
      <Header
        selectedView={currentView}
         customTheme={customTheme}
        handleStatClick={handleStatClick}
      />
      <div className="myTableViewChild">
        <DynamicIsland
          fields={processedFields ?? []}
          viewfieldsInstance={fieldWrapper} // Pass the instance of FieldWrapper
          rows={submissions} //NEEDS ROWS?
          allSubmissions={allSubmissions}
          logs={logs}
          tickerItems={tickerItems}
          availableReports={availableReports}
          availableMessages={currentForm?.meta?.messages2 || []} // N.B. ToDo DC - Updated to use messages2
          reviewers={reviewers}
          projectCode={currentProject ? currentProject.code : null}
          onSearchUpdate={setSearchTerm}
          onAssign={handleAssignments}
          onDelete={handleDelete}
          onAnalyse={handleAnalyseSelectedSubmissions}
          isAnalysingSelected={isAnalysingSelected}
          onCreateUser={handleNewUser}
          onMessageUpdate={handleMessageUpdate}
          onNewProject={handleNewProject}
          onCellUpdate={handleCellUpdate}
          onCycleComplete={handleCycleComplete}
          onTickerClose={handleTickerClose}
          onColumnsChange={handleFieldsSave}
          onViewsChange={handleViewsSave}
          onProjectsChange={handleProjectsSave}
          clearTickerItems={clearTickerItems}
          selectedView={currentView}
          selectedRows={selectedRows}
          treeData={treeData}
          formData={formData}
          cloneProjectTreeData={cloneProjectTreeData}
          adminTokens={appUser.adminTokens.tokens}
          selectedProjectToken={currentProject ? currentProject.token : null}
          selectedAccountToken={currentProject ? currentProject.accountToken : null}
          submissionToken={submissionToken}
          currentProjectName={currentProject ? currentProject?.name : null}
          currentProject={currentProject}
          currentForm={currentForm}
          assets={assets}
          reviewingTokens={reviewingTokens}
          activeReviewToken={activeReviewToken}
          setActiveReviewToken={setActiveReviewTokenHandler}
          onAccountChange={handleAccountChange}
          onProjectChange={handleProjectChange}
          onViewChange={handleViewChange}
          onSaveForm={handleSaveForm}
          onSaveAssets={handleSaveAssets}
          dictionaryResolvers={dictionaryResolvers}
          formDictionaries={currentForm ? currentForm.dictionaries : []}
          formPages={currentForm ? currentForm.pages : []}
          formLanguages={currentForm ? currentForm.meta.formLanguages : []}
          formName={currentForm ? currentForm.meta.name : null}
          formToken={currentForm ? currentForm.token : null}
          appUser={appUser}
          adminUsers={adminUsers}
          customTheme={customTheme}
          dawnSnapshot={user?.dawnSnapshot}
          fields={processedFields ?? []}
        />
      </div>
      <DataGrid
        fields={processedFields ?? []}
        key={`${currentProject?.token ?? "default"}-${currentView?.viewLabel ?? "defaultView"}-${filterConfig ?? "defaultFilter"}`}
        rows={Array.isArray(rows) ? rows : []}
        logs={logs}
        selectedRows={selectedRows}
        onColumnWidthChange={handleGridColumnWidthChange}
        onColumnOrderChange={handleGridColumnMove}
        onRowUpdate={handleRowUpdate}
        onCellUpdate={handleCellUpdate}
        onFileUpdate={handleFileUpdate}
        onReviewerChange={handleReviewerUpdate}
        onResetPassword={handleResetPassword}
        onSendPasswordResetEmail={handleSendPasswordResetEmail}
        onAdminise={handleSetProjectAdmin}
        onSelectionChange={handleSelectionChange}
        customTheme={customTheme}
        selectedView={currentView}
        projectCode={currentProject?.code}
        filterConfigType={filterConfig}
        searchTerm={searchTerm}
      />
    </div>
  );
}

function App() {
  return (
    <Authenticator hideSignUp>
      <SnackbarProvider>
        <DataProvider>
          <InnerApp />
        </DataProvider>
      </SnackbarProvider>
    </Authenticator>
  );
}

export default withAuthenticator(App);
